/**
 * atelier-realm.js — THE REALM. An open, wanderable Greek-mythology
 * landscape rendered as a living oil painting — now in FULL DAYLIGHT.
 *
 * Architecture (from the painterly-game research):
 *  - The world is a long horizontal strip. Each parallax plane is
 *    painted as brushstrokes into segment-tiled offscreen canvases
 *    (deterministic seeds, LRU per plane, NEGATIVE segment indices are
 *    normal at the world's west edge) and blitted per frame — per-frame
 *    cost is a handful of drawImage calls.
 *  - Value ladder (inverted from the old dusk build): COTTON dominant —
 *    a cotton sky with Turner bone/rose strokes and one restrained
 *    cherry sun; pale-maroon far mountains; parchment/bone warm ground;
 *    marble in bone/cotton with noir ink lines; cypress as noir-umber
 *    silhouettes. The frame reads LIGHT with dark accents.
 *  - CHARACTER VISIBILITY LAW: every figure passes through drawFigure()
 *    → charBackdrop() paints a soft cotton halo + grounded contact
 *    shadow BEHIND it; garments contrast the local ground (dark robes
 *    on the light day ground, cotton robes against dark landmarks —
 *    never mid on mid); a bone rim-light rides the sun side; interactive
 *    figures get a larger halo + ONE cherry accent item, decorative
 *    figures are hazier (atmospheric separation).
 *  - Two clocks: the motion clock (camera/characters, 60fps) and a slow
 *    boil clock that re-jitters the near ground plane at ~4Hz so the
 *    paint feels wet. Reduced motion freezes the boil, the sway and the
 *    animals and snaps the camera; the DOM walking tour below the
 *    canvas is always complete.
 *  - A living cast: the authored interactive NPCs (section blocks) plus
 *    a code-driven ambient cast (sailors, workers, musicians, an
 *    artisan, conversing pairs, a nymph, an animated statue, walking
 *    travellers) and animals (doves, goats, sheep, a cat, an owl,
 *    butterflies, bees, a lizard, a tortoise, dolphins, fish ripples) —
 *    simple deterministic state machines, proximity reactions, capped
 *    updates, offscreen skipped.
 *  - NPCs live in DOM speech bubbles (real text, palette contrast,
 *    max two at once, cooldowns + proximity hysteresis). Interaction
 *    prompts are real <button>s. Nothing readable is canvas pixels.
 *
 * Controls: ← → / A D to walk, E or Enter to speak, Esc to close.
 * Touch: tap the painting to walk there; hold the edge zones to keep
 * walking; tap the prompt button to speak. The stage never traps page
 * scroll (touch-action: pan-y lives in the section CSS).
 *
 * Audio (all optional-chained; silent if the audio module is absent):
 * setScene('realm-'+areaKey) on area change, surface-aware throttled
 * footsteps, event('dialogue') on dialogue open, event('bird') when a
 * flock scatters.
 *
 * Debug (?atlDebug=1): window.AtelierRealmDebug = { teleport(worldX),
 * npcRects() } — npcRects in CSS pixels relative to the stage canvas.
 */
(() => {
  const boot = (A) => {
    A.register('realm', (root) => {
      const stage = root.querySelector('[data-realm-stage]');
      const canvas = root.querySelector('[data-realm-canvas]');
      const bubbleHost = root.querySelector('[data-realm-bubbles]');
      const promptBtn = root.querySelector('[data-realm-prompt]');
      const dialoguePanel = root.querySelector('[data-realm-dialogue]');
      const dataEl = root.querySelector('[data-realm-data]');
      if (!canvas || !stage) return () => {};

      const ctx = canvas.getContext('2d');
      if (!ctx) return () => {};

      /* ---------- Data (NPCs, areas) from the section, not hardcoded */
      let DATA = { npcs: [], areas: [] };
      try {
        if (dataEl) DATA = JSON.parse(dataEl.textContent);
      } catch (e) {
        /* fall back to empty world */
      }

      const { clamp, lerp, hash } = A.helpers;
      const TAU = Math.PI * 2;

      /* Deterministic per-element rng. */
      const rng = (seed) => {
        let n = seed * 1000 + 17;
        return () => {
          n += 1;
          return hash(n);
        };
      };

      /* ---------- Palette — the four pigments + honest mixes only.
         Every rgb() below is a literal lerp of Cotton #EDEBDD, Cherry
         #810100, Maroon #630000, Noir #1B1717 (mix ratios noted). */
      const P = {
        noir: '#1b1717',
        noirDeep: '#120e0e', // noir + black of the ground
        noirWarm: '#241c1b', // noir + a knife of maroon
        maroon: '#630000',
        maroonDark: '#3d0806', // maroon + noir
        umber: '#3a2422', // maroon + noir, warmer
        cherry: '#810100',
        cotton: '#edebdd',
        parchment: '#f4f2e8', // cotton + white light
        bone: '#ddd8c6', // cotton dulled with noir
        cottonShadow: '#d8d2bd', // warm cotton-shadow
        rose: '#d9a9a2', // cherry + cotton
        skyHorizon: 'rgb(226,199,189)', // cotton 45% → rose
        mountainFar: 'rgb(204,179,168)', // cotton + 24% maroon
        mountainNear: 'rgb(182,141,133)', // cotton + 40% maroon
        warmGrey: 'rgb(140,138,130)', // cotton + 46% noir — foliage, never green
        warmGreyLight: 'rgb(174,171,162)', // cotton + 30% noir
        ground: 'rgb(222,220,207)', // cotton + 7% noir
        groundShade: 'rgb(206,203,191)', // cotton + 15% noir
        cella: 'rgb(49,16,16)', // noir + 30% maroon — temple doorway dark
        robeHazy: 'rgb(86,66,62)', // umber lifted 15% toward cotton
      };

      /* ---------- World geometry */
      const WORLD_W = DATA.worldWidth || 6200;
      let W = 0;
      let H = 0;
      let dpr = 1;
      let groundBase = 0;

      /* Rolling ground line — the walkable surface IS the nearest hill.
         Single ground truth for the painted crest AND every character. */
      const groundAt = (x) => {
        const u = x * 0.0016;
        return (
          groundBase +
          Math.sin(u * 2.1) * 14 +
          Math.sin(u * 0.7 + 2.3) * 26 +
          Math.sin(u * 4.3 + 5.1) * 6
        );
      };

      /* ---------- Painterly kit (stroke primitives) */
      const taper = (g, x0, y0, x1, y1, w, color, alpha, segs) => {
        const n = segs || 8;
        g.strokeStyle = color;
        g.globalAlpha = alpha;
        g.lineCap = 'round';
        for (let i = 0; i < n; i++) {
          const t0 = i / n;
          const t1 = (i + 1) / n;
          g.lineWidth = Math.max(w * Math.sin(((t0 + t1) / 2) * Math.PI), 0.6);
          g.beginPath();
          g.moveTo(lerp(x0, x1, t0), lerp(y0, y1, t0));
          g.lineTo(lerp(x0, x1, t1), lerp(y0, y1, t1));
          g.stroke();
        }
        g.globalAlpha = 1;
      };

      const daub = (g, x, y, rx, ry, ang, color, alpha) => {
        g.save();
        g.translate(x, y);
        g.rotate(ang);
        g.scale(1, ry / rx);
        g.globalAlpha = alpha;
        g.fillStyle = color;
        g.beginPath();
        g.arc(0, 0, rx, 0, TAU);
        g.fill();
        g.restore();
        g.globalAlpha = 1;
      };

      /* =================================================================
         CHARACTER VISIBILITY SYSTEM
         ================================================================= */

      /* Soft cotton-light halo painted BEHIND a figure + a grounded
         contact shadow at the feet. On the light day ground the halo
         lifts the local value to near-parchment so dark garments pop;
         against dark landmarks it carves the figure out of the mass. */
      const charBackdrop = (g, x, y, r, strength) => {
        let s = strength == null ? 1 : strength;
        if (s <= 0) return;
        /* Floors in CSS pixels / alpha: separation must survive when a
           figure draws small (390px viewports) — the halo never shrinks
           below 36px radius nor below ~72% strength. */
        const rr = Math.max(r, 36);
        s = Math.max(s, 0.72);
        const cy = y - rr * 0.58;
        const halo = g.createRadialGradient(x, cy, rr * 0.08, x, cy, rr);
        halo.addColorStop(0, 'rgba(244,242,232,' + Math.min(0.5 * s, 0.72).toFixed(3) + ')');
        halo.addColorStop(0.65, 'rgba(244,242,232,' + Math.min(0.2 * s, 0.3).toFixed(3) + ')');
        halo.addColorStop(1, 'rgba(244,242,232,0)');
        g.fillStyle = halo;
        g.beginPath();
        g.arc(x, cy, rr, 0, TAU);
        g.fill();
        /* grounded contact shadow — the figure stands ON the paint */
        g.save();
        g.globalAlpha = Math.min(0.1 + 0.16 * s, 0.34);
        g.fillStyle = P.noir;
        g.beginPath();
        g.ellipse(x, y + 2, rr * 0.42, rr * 0.1, 0, 0, TAU);
        g.fill();
        g.restore();
      };

      /* Identifying hand props (drawn in figure-local space). */
      const drawAttr = (g, attr, alpha) => {
        if (!attr || attr === 'none') return;
        const a = alpha == null ? 1 : alpha;
        if (attr === 'lyre') {
          g.strokeStyle = P.umber;
          g.globalAlpha = a;
          g.lineWidth = 2;
          g.beginPath();
          g.arc(-12, -38, 7, Math.PI * 0.9, Math.PI * 2.1);
          g.stroke();
          for (let i = 0; i < 4; i++) {
            g.beginPath();
            g.moveTo(-17 + i * 3, -42);
            g.lineTo(-16 + i * 3, -32);
            g.stroke();
          }
          g.globalAlpha = 1;
        } else if (attr === 'staff') {
          taper(g, 13, -62, 13, 1, 3, P.umber, 0.95 * a, 3);
        } else if (attr === 'flame') {
          /* a raised torch — the flame is this figure's cherry item */
          taper(g, 12, -30, 12, -45, 2.4, P.umber, 0.9 * a, 3);
          daub(g, 12, -48, 4, 6, 0, P.cherry, 0.85 * a);
          daub(g, 12, -51, 2.2, 3.2, 0, P.rose, 0.8 * a);
        } else if (attr === 'scroll') {
          g.save();
          g.translate(11, -32);
          g.rotate(-0.35);
          g.globalAlpha = 0.95 * a;
          g.fillStyle = P.parchment;
          g.fillRect(-6, -3, 12, 6);
          g.globalAlpha = 0.7 * a;
          g.strokeStyle = P.noir;
          g.lineWidth = 0.9;
          g.beginPath();
          g.moveTo(-4, -1);
          g.lineTo(4, -1);
          g.moveTo(-4, 1);
          g.lineTo(3, 1);
          g.stroke();
          g.restore();
          g.globalAlpha = 1;
        } else if (attr === 'amphora') {
          daub(g, 13, -14, 4.6, 6.4, 0, P.umber, 0.9 * a);
          taper(g, 13, -21, 13, -18, 3, P.umber, 0.9 * a, 2);
          g.strokeStyle = P.noir;
          g.globalAlpha = 0.5 * a;
          g.lineWidth = 1;
          g.beginPath();
          g.arc(13, -18, 4, Math.PI * 1.1, Math.PI * 1.9);
          g.stroke();
          g.globalAlpha = 1;
        } else if (attr === 'laurel' || attr === 'sprig') {
          const sx = attr === 'laurel' ? -12 : 12;
          const sy = attr === 'laurel' ? -40 : -32;
          taper(g, sx, sy + 6, sx - 3, sy - 6, 1.6, P.warmGrey, 0.9 * a, 3);
          for (let i = 0; i < 4; i++) {
            daub(g, sx - 1 - i * 0.6, sy + 3 - i * 3, 2.2, 1.1, -0.5, i % 2 ? P.warmGreyLight : P.bone, 0.8 * a);
            daub(g, sx - 3 + i * 0.4, sy + 1 - i * 3, 2, 1, 0.6, P.warmGrey, 0.7 * a);
          }
        } else if (attr === 'mask') {
          /* a theatre mask held beside the head */
          taper(g, 8, -40, 13, -48, 2, P.umber, 0.85 * a, 2);
          daub(g, -13, -46, 5, 6.6, 0.1, P.bone, 0.95 * a);
          g.fillStyle = P.noirDeep;
          g.globalAlpha = 0.9 * a;
          g.beginPath();
          g.ellipse(-15, -48, 1.3, 1.6, 0, 0, TAU);
          g.ellipse(-11, -48, 1.3, 1.6, 0, 0, TAU);
          g.fill();
          g.beginPath();
          g.ellipse(-13, -42.5, 1.8, 2.4, 0, 0, TAU);
          g.fill();
          g.globalAlpha = 1;
        }
      };

      /* One figure, stroke-built. EVERY human in the realm goes through
         here so the visibility law is enforced in one place.
         o: { x, y (ground point, screen px), s scale, face ±1,
              robe 'dark'|'light', hazy 0..1, alpha, sway px, bob px,
              lean rad-ish, phase (walk cycle, 0 = standing),
              pose 'idle'|'haul'|'pick'|'stoop'|'chisel'|'strum'|'gesture',
              strike 0..1 (chisel), gest 0..1 (gesture/strum),
              attr, seed, interactive, name (→ npcRects) } */
      let figRects = [];
      const drawFigure = (g, o) => {
        const s = o.s || 1;
        const hz = o.hazy || 0;
        const face = o.face || 1;
        const sway = o.sway || 0;
        const bob = o.bob || 0;
        const inter = !!o.interactive;
        const alpha = (o.alpha == null ? 1 : o.alpha) * (1 - hz * 0.28);
        /* interactive figures read STRONGER than ambient ones: a wider
           halo pushed past unit strength (charBackdrop caps it) */
        charBackdrop(g, o.x, o.y, (inter ? 44 : 31) * s, (inter ? 1.3 : 0.58) * alpha);
        /* report a rect only when the figure sits far enough inside the
           canvas to be pixel-sampled (edge-clipped figures would hand QA
           slivers of empty ground) */
        if (o.name && o.x > 50 * s && o.x < W - 50 * s) {
          figRects.push({
            name: o.name,
            x: Math.round(o.x - 17 * s),
            y: Math.round(o.y + bob - 68 * s),
            w: Math.round(34 * s),
            h: Math.round(70 * s),
            interactive: inter,
          });
        }
        const robeLight = o.robe === 'light';
        const robeRose = o.robe === 'rose'; /* the nymph: rose-wash interior */
        /* garment value law: dark garments on the light day ground,
           cotton garments against dark landmarks; hazier figures drift
           toward warm umber-grey but never toward the ground's value */
        const cMain = robeLight ? P.cotton : robeRose ? P.rose : hz > 0.25 ? P.robeHazy : P.noirWarm;
        const cMid = robeLight ? P.bone : robeRose ? P.rose : P.maroon;
        const cTrim = robeLight ? P.umber : robeRose ? P.maroon : P.cotton;
        /* the smallest figures (and every figure on a narrow canvas)
           get louder garment trim so separation survives at 390px */
        const small = s < 0.94 || W < 520;
        g.save();
        g.translate(o.x, o.y + bob);
        g.save();
        g.scale(face * s, s);
        const lean = o.lean || 0;
        const lp = o.phase ? Math.sin(o.phase * 9) * 6 : 0;
        /* legs first — walkers show them under the hem */
        taper(g, -3, -10, -3 - lp, 1, 5, P.noirDeep, 0.95 * alpha, 3);
        taper(g, 4, -10, 4 + lp, 1, 5, P.noirDeep, 0.95 * alpha, 3);
        /* the robe: one broad stroke, one mid tone, one trim line */
        taper(g, sway + lean * 26, -50, 0, -6, 24, cMain, 0.95 * alpha, 6);
        taper(g, sway * 0.7 + lean * 20, -46, 1, -10, 13, cMid, (robeLight ? 0.45 : robeRose ? 0.3 : 0.5) * alpha, 5);
        taper(g, sway * 0.4 - 6, -13, 6, -9, small ? 3.2 : 2.4, cTrim, Math.min(1, (robeLight ? 0.55 : robeRose ? 0.45 : 0.6) + (small ? 0.25 : 0)) * alpha, 3);
        if (inter && robeLight) {
          /* cotton-robed interactive figures (the Priestess) also get a
             noir ink contour so they read where marble peeks through */
          taper(g, -12 + sway * 0.5, -48, -9, -8, 1.6, P.noir, 0.5 * alpha, 4);
          taper(g, 12 + sway * 0.5, -50, 10, -8, 1.4, P.noir, 0.42 * alpha, 4);
        }
        /* arms per pose */
        if (o.pose === 'haul') {
          const pull = o.gest == null ? 0 : o.gest;
          taper(g, 3, -40, 16 + pull * 3, -31, 4, cMain, alpha, 3);
          taper(g, 2, -36, 15 + pull * 3, -27, 3.2, cMid, alpha * 0.6, 3);
        } else if (o.pose === 'pick') {
          const reach = Math.sin(o.phase || 0) * 4;
          taper(g, 2, -44, 12 + reach, -60 - reach, 3.6, cMain, alpha, 3);
        } else if (o.pose === 'stoop') {
          taper(g, 4, -32, 13, -15, 3.6, cMain, alpha, 3);
        } else if (o.pose === 'chisel') {
          const k = o.strike || 0;
          taper(g, 3, -42, 14, -48 - k * 9, 3.8, cMain, alpha, 3);
          taper(g, 3, -38, 14, -33, 3.2, cMain, alpha, 3);
        } else if (o.pose === 'strum') {
          taper(g, 2, -40, -8, -37, 3.4, cMain, alpha, 3);
        } else if (o.pose === 'gesture') {
          const k = o.gest == null ? 0 : o.gest;
          taper(g, 3, -40, 12, -42 - k * 8, 3.4, cMain, alpha, 3);
        }
        /* head — pose can bow it */
        let hdx = 0;
        let hdy = 0;
        if (o.pose === 'stoop') {
          hdx = 8;
          hdy = 8;
        } else if (o.pose === 'pick') {
          hdx = 3;
          hdy = -1;
        }
        daub(g, sway * 0.9 + hdx, -58 + hdy, 6.6, 7.6, 0, P.bone, 0.97 * alpha);
        daub(g, sway * 0.9 + hdx + 2.2, -59.5 + hdy, 2.6, 3.2, 0, P.rose, 0.4 * alpha);
        if (robeLight) {
          /* a cotton veil over the crown */
          taper(g, sway * 0.9 + hdx - 6, -64 + hdy, sway * 0.9 + hdx + 6, -63 + hdy, 5, P.cotton, 0.85 * alpha, 3);
          taper(g, sway * 0.9 + hdx - 5, -63 + hdy, sway * 0.9 + hdx - 6, -52 + hdy, 3, P.bone, 0.5 * alpha, 3);
        } else {
          taper(g, sway * 0.9 + hdx - 6, -63 + hdy, sway * 0.9 + hdx + 6, -61 + hdy, 5.5, P.noir, 0.9 * alpha, 3);
        }
        drawAttr(g, o.attr, alpha);
        /* interactive figures carry the area's ONE cherry item
           (the torch-bearers' flame already is theirs) */
        if (inter && o.attr !== 'flame') {
          if ((o.seed || 0) % 2) daub(g, 1, -44, 2.6, 2.6, 0, P.cherry, 0.9);
          else taper(g, -5, -42, 4, -18, 3.4, P.cherry, 0.85, 4);
        }
        g.restore();
        /* rim light on the sun side (the cherry sun hangs screen-right) */
        taper(g, 8 * s, -48 * s, 9.5 * s, -12 * s, 2.2, P.parchment, (inter ? 0.55 : 0.32) * alpha, 4);
        g.restore();
      };

      /* ---------- Landmark painters (into cached plane segments) */

      /* Cypress — a dark vertical flame; THE noir-umber silhouette of
         the daylight world. pale=true gives the hazy distant version. */
      const cypress = (g, x, baseY, h, seed, pale) => {
        const r = rng(seed);
        const cA = pale ? P.warmGrey : P.noirDeep;
        const cB = pale ? P.warmGreyLight : P.umber;
        for (let i = 0; i < 16; i++) {
          const t = i / 16;
          const w = h * 0.16 * Math.sin(Math.min(t * 1.35, 1) * Math.PI) + 2;
          taper(
            g,
            x + (r() - 0.5) * 6,
            baseY - t * h,
            x + (r() - 0.5) * 6,
            baseY - Math.min(t + 0.14, 1) * h,
            w,
            r() > 0.4 ? cA : cB,
            pale ? 0.6 : 0.85,
            3
          );
        }
      };

      /* Olive tree — twisted umber trunk, warm-grey daub canopy with
         bone light on top (silver-leaf without silver, green-free). */
      const olive = (g, x, baseY, s, seed) => {
        const r = rng(seed);
        taper(g, x, baseY, x - 6 * s, baseY - 26 * s, 7 * s, P.umber, 0.9, 5);
        taper(g, x - 6 * s, baseY - 26 * s, x + 4 * s, baseY - 44 * s, 5 * s, P.umber, 0.9, 5);
        for (let c = 0; c < 3; c++) {
          const cx = x + (r() - 0.5) * 40 * s;
          const cy = baseY - (40 + r() * 22) * s;
          for (let i = 0; i < 14; i++) {
            const ang = r() * TAU;
            const d = r() * 16 * s;
            const top = Math.sin(ang) < -0.3;
            daub(
              g,
              cx + Math.cos(ang) * d,
              cy + Math.sin(ang) * d * 0.7,
              (4 + r() * 5) * s,
              (2.5 + r() * 3) * s,
              r() * 0.8 - 0.4,
              top ? P.bone : P.warmGrey,
              top ? 0.55 : 0.7
            );
          }
        }
      };

      /* Doric temple — bone/cotton marble with noir ink lines: stylobate,
         tapered columns with entasis, architrave, shallow pediment, and
         (intact temples) a dark cella doorway — the shadow that lets a
         cotton-robed figure stand out in front of the marble. */
      const temple = (g, x, baseY, w, h, cols, seed, ruined) => {
        const r = rng(seed);
        const colH = h * 0.62;
        const styH = h * 0.08;
        /* soft ground shadow so the marble sits IN the landscape */
        daub(g, x, baseY - 2, w * 0.62, styH * 0.9, 0, P.umber, 0.14);
        if (!ruined) {
          /* the cella dark behind the middle columns — deep and WIDE
             enough that a cotton-robed keeper reads against it across
             her whole patrol range in front of the temple */
          g.fillStyle = P.cella;
          g.globalAlpha = 0.96;
          g.fillRect(x - w * 0.3, baseY - styH - colH * 0.9, w * 0.6, colH * 0.9);
          g.globalAlpha = 1;
          /* a noir-deep doorway core, twice as dark as the cella wash */
          g.fillStyle = P.noirDeep;
          g.globalAlpha = 0.85;
          g.fillRect(x - w * 0.12, baseY - styH - colH * 0.82, w * 0.24, colH * 0.82);
          g.globalAlpha = 1;
          /* irregular painted jambs + lintel so the dark reads as an
             opening, not a slab */
          taper(g, x - w * 0.12, baseY - styH - colH * 0.8, x - w * 0.115, baseY - styH - 2, 2.2, P.maroonDark, 0.7, 4);
          taper(g, x + w * 0.12, baseY - styH - colH * 0.78, x + w * 0.115, baseY - styH - 2, 2, P.maroonDark, 0.65, 4);
          taper(g, x - w * 0.15, baseY - styH - colH * 0.82, x + w * 0.15, baseY - styH - colH * 0.83, 3, P.umber, 0.6, 3);
        }
        g.fillStyle = P.bone;
        g.globalAlpha = 0.95;
        g.fillRect(x - w / 2 - 8, baseY - styH, w + 16, styH);
        g.globalAlpha = 1;
        const gap = w / (cols - 1);
        for (let i = 0; i < cols; i++) {
          if (ruined && r() < 0.28 && i !== 0 && i !== cols - 1) {
            /* fallen column: two drums, ink-shadowed */
            const dx = x - w / 2 + i * gap;
            daub(g, dx + 14, baseY - styH - 5, 9, 5, 0.3, P.bone, 0.9);
            daub(g, dx + 30, baseY - styH - 4, 8, 4.5, -0.2, P.bone, 0.85);
            taper(g, dx + 6, baseY - styH - 2, dx + 22, baseY - styH - 1, 1.2, P.noir, 0.3, 2);
            continue;
          }
          const cx = x - w / 2 + i * gap;
          const cw = (i === 0 || i === cols - 1 ? 7.5 : 7) * (w / 190);
          /* entasis: slight mid bulge, tapering up — cotton light face */
          g.fillStyle = P.cotton;
          for (let s2 = 0; s2 < 5; s2++) {
            const t = s2 / 5;
            const ww = cw * (1 - t * 0.22) * (1 + Math.sin(t * Math.PI) * 0.08);
            g.fillRect(cx - ww / 2, baseY - styH - colH * (t + 0.2), ww, colH * 0.22);
          }
          /* bone shade on the west face + a noir ink contour east */
          g.globalAlpha = 0.55;
          g.fillStyle = P.bone;
          g.fillRect(cx - cw / 2, baseY - styH - colH, cw * 0.34, colH);
          g.globalAlpha = 1;
          taper(g, cx + cw / 2, baseY - styH - colH + 3, cx + cw / 2, baseY - styH - 2, 1.1, P.noir, 0.3, 3);
          g.fillStyle = P.cotton;
          g.fillRect(cx - cw * 0.75, baseY - styH - colH - 4, cw * 1.5, 4); // capital
        }
        /* stylobate ink line */
        taper(g, x - w / 2 - 8, baseY - styH, x + w / 2 + 8, baseY - styH + 1, 1.4, P.noir, 0.32, 4);
        if (!ruined) {
          g.fillStyle = P.cotton;
          g.fillRect(x - w / 2 - 6, baseY - styH - colH - 12, w + 12, 8); // architrave
          g.beginPath(); // pediment ~1:4 pitch
          g.moveTo(x - w / 2 - 10, baseY - styH - colH - 12);
          g.lineTo(x, baseY - styH - colH - 12 - w * 0.13);
          g.lineTo(x + w / 2 + 10, baseY - styH - colH - 12);
          g.closePath();
          g.fill();
          /* pediment ink outline — the drawn line of the painting */
          g.strokeStyle = P.noir;
          g.globalAlpha = 0.35;
          g.lineWidth = 1.3;
          g.beginPath();
          g.moveTo(x - w / 2 - 10, baseY - styH - colH - 12);
          g.lineTo(x, baseY - styH - colH - 12 - w * 0.13);
          g.lineTo(x + w / 2 + 10, baseY - styH - colH - 12);
          g.stroke();
          g.globalAlpha = 1;
        }
        /* one warm rim stroke on the lit (sun-side, east) edge */
        taper(g, x + w / 2, baseY - styH - colH, x + w / 2, baseY - styH, 2.5, P.parchment, 0.75, 4);
      };

      /* Amphitheatre — RUINED tiers stepping down a hillside. Never a
         geometric rainbow: each ring survives only as broken fragments
         (missing spans, wandering centres, varied width and alpha) with
         dry-brush overstrokes, ink shards, and a couple of fallen arc
         chunks lying on the slope below. Seeded per landmark so seam
         neighbours lay identical strokes. */
      const amphitheatre = (g, x, baseY, s, seed) => {
        const r = rng(seed * 7 + 3);
        g.lineCap = 'round';
        for (let i = 0; i < 9; i++) {
          const rr = (26 + i * 12) * s;
          const cBase = i < 4 ? P.bone : P.warmGreyLight;
          const wobX = x + (r() - 0.5) * 4 * s;
          const wobY = baseY + 6 * s + (r() - 0.5) * 3 * s;
          let a = Math.PI * (1.05 + r() * 0.07);
          const end = Math.PI * (1.88 + r() * 0.06);
          while (a < end) {
            const span = 0.16 + r() * 0.46; /* surviving fragment */
            const gap = 0.05 + r() * 0.34; /* collapsed span */
            const a1 = Math.min(a + span, end);
            if (r() > 0.2) {
              g.strokeStyle = cBase;
              g.globalAlpha = (i < 4 ? 0.42 : 0.26) + r() * 0.38;
              g.lineWidth = (1.8 + r() * 3.6) * s;
              g.beginPath();
              g.arc(wobX, wobY, rr, a, a1);
              g.stroke();
              /* dry-brush scumble riding the fragment */
              if (r() > 0.42 && a1 - a > 0.14) {
                g.strokeStyle = r() > 0.5 ? P.cottonShadow : P.parchment;
                g.globalAlpha = 0.18 + r() * 0.2;
                g.lineWidth = (0.8 + r() * 1.3) * s;
                g.beginPath();
                g.arc(wobX, wobY - 1.2 * s, rr - 1.4 * s, a + 0.05, a1 - 0.04);
                g.stroke();
              }
              /* a shard of ink shadow under some fragments */
              if (r() > 0.45 && a1 - a > 0.12) {
                g.strokeStyle = P.noir;
                g.globalAlpha = 0.1 + r() * 0.13;
                g.lineWidth = 1;
                g.beginPath();
                g.arc(wobX, wobY + 2.4 * s, rr, a + 0.04, a1 - 0.03);
                g.stroke();
              }
            }
            a = a1 + gap;
          }
        }
        g.globalAlpha = 1;
        /* fallen arc chunks lying on the ground below the tiers */
        for (let f = 0; f < 2; f++) {
          const fx = x + (f ? 78 : -92) * s + (r() - 0.5) * 22 * s;
          const fy = baseY + (14 + r() * 8) * s;
          /* contact shadow first — the chunk lies ON the slope */
          g.globalAlpha = 0.14;
          g.fillStyle = P.umber;
          g.beginPath();
          g.ellipse(fx, fy + 1.5 * s, 15 * s, 3 * s, 0, 0, TAU);
          g.fill();
          const tilt = (r() - 0.5) * 0.5;
          g.strokeStyle = P.bone;
          g.globalAlpha = 0.72 + r() * 0.15;
          g.lineWidth = (2.6 + r() * 2) * s;
          g.beginPath();
          g.arc(fx, fy + 14 * s, 17 * s, Math.PI * 1.18 + tilt, Math.PI * 1.58 + tilt);
          g.stroke();
          g.strokeStyle = P.noir;
          g.globalAlpha = 0.22;
          g.lineWidth = 1;
          g.beginPath();
          g.arc(fx, fy + 15.6 * s, 17 * s, Math.PI * 1.22 + tilt, Math.PI * 1.52 + tilt);
          g.stroke();
        }
        g.globalAlpha = 1;
      };

      /* Statue — a bone figure on an umber plinth. VISIBILITY LAW: the
         marble is carved out of a noir-umber laurel mass painted behind
         it (bone never sits on bone), with a stronger ink contour and a
         grounded contact shadow. */
      const statue = (g, x, baseY, s, seed) => {
        const r = rng(seed);
        /* the dark separation backdrop: a laurel/cypress foliage mass */
        for (let i = 0; i < 14; i++) {
          const ang = r() * TAU;
          const d = r() * 20 * s;
          daub(
            g,
            x + Math.cos(ang) * d * 0.85,
            baseY - (30 + r() * 26) * s + Math.sin(ang) * d * 0.5,
            (7 + r() * 9) * s,
            (4.5 + r() * 5) * s,
            (r() - 0.5) * 0.9,
            r() > 0.62 ? P.warmGrey : r() > 0.3 ? P.umber : P.noirDeep,
            0.5 + r() * 0.3
          );
        }
        taper(g, x - 2 * s, baseY, x - 5 * s, baseY - 54 * s, 6 * s, P.noirDeep, 0.55, 4);
        /* grounded contact shadow under the plinth */
        g.save();
        g.globalAlpha = 0.2;
        g.fillStyle = P.noir;
        g.beginPath();
        g.ellipse(x, baseY + 1, 16 * s, 3.4 * s, 0, 0, TAU);
        g.fill();
        g.restore();
        g.fillStyle = P.umber;
        g.fillRect(x - 10 * s, baseY - 8 * s, 20 * s, 8 * s);
        daub(g, x, baseY - 34 * s, 6 * s, 7 * s, 0, P.bone, 0.95); // head
        taper(g, x, baseY - 28 * s, x, baseY - 8 * s, 14 * s, P.bone, 0.92, 5); // body drape
        /* stronger noir ink contour, both flanks + the head */
        taper(g, x + 6 * s, baseY - 30 * s, x + 6.5 * s, baseY - 10 * s, 1.8, P.noir, 0.55, 3);
        taper(g, x - 6.5 * s, baseY - 29 * s, x - 6 * s, baseY - 10 * s, 1.3, P.noir, 0.4, 3);
        g.strokeStyle = P.noir;
        g.globalAlpha = 0.45;
        g.lineWidth = 1.1;
        g.beginPath();
        g.arc(x, baseY - 34 * s, 6.4 * s, -0.7, 1.9);
        g.stroke();
        g.globalAlpha = 1;
        taper(g, x - 3 * s, baseY - 26 * s, x + 2 * s, baseY - 12 * s, 2 * s, P.cottonShadow, 0.6, 4);
        /* rim light on the sun side */
        taper(g, x + 5 * s, baseY - 30 * s, x + 5 * s, baseY - 11 * s, 1.6 * s, P.parchment, 0.7, 3);
      };

      /* ---------- Plane painters (per segment) */
      const SEG_W = 1400; // world units per painted segment

      /* Landmark seam rule: every plane painter tests
         area.x within ±400 beyond its segment edges so both seam
         neighbours paint the same landmark. ALL area furniture must
         therefore stay inside area.x ± 380. */
      const SEAM = 400;

      const paintSky = (g, w, h) => {
        /* cotton sky — parchment zenith warming toward a rose horizon */
        const grd = g.createLinearGradient(0, 0, 0, h);
        grd.addColorStop(0, P.parchment);
        grd.addColorStop(0.42, P.cotton);
        grd.addColorStop(0.78, P.bone);
        grd.addColorStop(1, P.skyHorizon);
        g.fillStyle = grd;
        g.fillRect(0, 0, w, h);
        /* Turner: long dragged low-alpha strokes, denser near the light */
        const r = rng(9);
        for (let i = 0; i < 52; i++) {
          const y = Math.pow(r(), 0.72) * h * 0.86;
          const pick = r();
          taper(
            g,
            r() * w - 80,
            y,
            r() * w + 180,
            y + (r() - 0.5) * 12,
            6 + r() * 18,
            pick > 0.62 ? P.bone : pick > 0.34 ? P.rose : pick > 0.12 ? P.mountainFar : P.parchment,
            0.05 + r() * 0.08,
            5
          );
        }
        /* a few high bone cloud scumbles */
        for (let c = 0; c < 4; c++) {
          const cx = r() * w;
          const cy = h * (0.08 + r() * 0.2);
          for (let i = 0; i < 8; i++) {
            daub(g, cx + (r() - 0.5) * 130, cy + (r() - 0.5) * 22, 26 + r() * 30, 8 + r() * 8, (r() - 0.5) * 0.3, r() > 0.5 ? P.parchment : P.bone, 0.1 + r() * 0.1);
          }
        }
        /* THE one cherry event of the sky: a small restrained sun,
           rose-hazed, hanging east. */
        const sx = w * 0.62;
        const sy = h * 0.28;
        const glow = g.createRadialGradient(sx, sy, 0, sx, sy, h * 0.2);
        glow.addColorStop(0, 'rgba(217,169,162,0.5)');
        glow.addColorStop(0.55, 'rgba(217,169,162,0.16)');
        glow.addColorStop(1, 'rgba(217,169,162,0)');
        g.fillStyle = glow;
        g.fillRect(sx - h * 0.22, sy - h * 0.22, h * 0.44, h * 0.44);
        daub(g, sx, sy, h * 0.03, h * 0.03, 0, P.cherry, 0.85);
        daub(g, sx - h * 0.008, sy - h * 0.009, h * 0.008, h * 0.008, 0, P.rose, 0.7);
      };

      const paintFar = (g, segIndex, w, h) => {
        const r = rng(segIndex * 31 + 5);
        /* two pale-maroon ridges melting into the cotton haze */
        g.fillStyle = P.mountainFar;
        g.beginPath();
        g.moveTo(0, h);
        for (let x = 0; x <= w; x += 40) {
          g.lineTo(x, h * 0.5 + Math.sin((segIndex * SEG_W + x) * 0.003) * h * 0.09);
        }
        g.lineTo(w, h);
        g.closePath();
        g.fill();
        g.fillStyle = P.mountainNear;
        g.beginPath();
        g.moveTo(0, h);
        for (let x = 0; x <= w; x += 40) {
          g.lineTo(x, h * 0.62 + Math.sin((segIndex * SEG_W + x) * 0.004) * h * 0.1);
        }
        g.lineTo(w, h);
        g.closePath();
        g.fill();
        /* bone light kissing the ridge lines */
        for (let i = 0; i < 7; i++) {
          const x = r() * w;
          const y = h * 0.62 + Math.sin((segIndex * SEG_W + x) * 0.004) * h * 0.1;
          taper(g, x - 30, y + 2, x + 40 + r() * 40, y + (r() - 0.5) * 6, 4 + r() * 5, P.bone, 0.28, 3);
        }
        /* a far temple or ruin on some ridges — bone with faint ink */
        if (hash(segIndex * 7.7) > 0.5) {
          temple(g, w * (0.3 + r() * 0.4), h * 0.66, 90, 54, 4, segIndex * 13, hash(segIndex * 3.1) > 0.6);
        }
        for (let i = 0; i < 5; i++) {
          cypress(g, r() * w, h * (0.68 + r() * 0.06), 40 + r() * 30, segIndex * 100 + i, true);
        }
      };

      const paintMid = (g, segIndex, w, h) => {
        const r = rng(segIndex * 57 + 11);
        /* warm bone hills */
        g.fillStyle = P.bone;
        g.beginPath();
        g.moveTo(0, h);
        for (let x = 0; x <= w; x += 30) {
          g.lineTo(x, h * 0.55 + Math.sin((segIndex * SEG_W + x) * 0.006 + 1.7) * h * 0.12);
        }
        g.lineTo(w, h);
        g.closePath();
        g.fill();
        /* slope strokes following the hill + pale maroon washes */
        for (let i = 0; i < 26; i++) {
          const x = r() * w;
          const y = h * (0.6 + r() * 0.3);
          taper(g, x, y, x + 30 + r() * 50, y + (r() - 0.5) * 10, 5 + r() * 9, r() > 0.55 ? P.cottonShadow : P.groundShade, 0.4, 4);
        }
        for (let i = 0; i < 8; i++) {
          const x = r() * w;
          const y = h * (0.62 + r() * 0.26);
          taper(g, x, y, x + 60 + r() * 60, y + (r() - 0.5) * 8, 8 + r() * 10, 'rgba(99,0,0,0.07)', 1, 3);
        }
        /* crest light */
        for (let i = 0; i < 5; i++) {
          const x = r() * w;
          const y = h * 0.55 + Math.sin((segIndex * SEG_W + x) * 0.006 + 1.7) * h * 0.12;
          taper(g, x, y + 1, x + 50 + r() * 60, y + 2, 3 + r() * 4, P.parchment, 0.35, 3);
        }
        /* area furniture by world position (seam rule ±SEAM) */
        const worldX0 = segIndex * SEG_W;
        (DATA.areas || []).forEach((area) => {
          if (area.x < worldX0 - SEAM || area.x > worldX0 + w + SEAM) return;
          const ax = area.x - worldX0;
          if (area.kind === 'grove') {
            /* seeded from the landmark (not the segment) so seam
               neighbours paint identical trees */
            const gr = rng(Math.round(area.x) * 3 + 1);
            for (let i = 0; i < 6; i++) olive(g, ax - 200 + i * 80 + gr() * 30, h * 0.72, 0.9 + gr() * 0.5, Math.round(area.x) * 900 + i);
          } else if (area.kind === 'amphitheatre') {
            amphitheatre(g, ax, h * 0.62, 1.1, Math.round(area.x));
          } else if (area.kind === 'statues') {
            statue(g, ax - 70, h * 0.74, 1, Math.round(area.x) * 3);
            statue(g, ax + 60, h * 0.75, 0.85, Math.round(area.x) * 5);
          } else if (area.kind === 'harbor' || area.kind === 'gate') {
            /* THE BAY — painted water, no straight edge anywhere.
               Seeded from the landmark (not the segment) so seam
               neighbours lay identical strokes. The wash runs to the
               bottom of the plane so the near ground always overlaps
               its lower edge — the sea can never float. */
            const sr = rng(Math.round(area.x) * 3 + 7);
            const seaTop = h * 0.54; /* soft horizon under the mountains */
            const half = 360;
            const deep = h - seaTop;
            /* base wash: an irregular cove — wavy horizon, flanks that
               wander outward as the water nears the viewer */
            g.save();
            g.beginPath();
            g.moveTo(ax - half * 0.62, seaTop + 3);
            for (let i = 1; i <= 12; i++) {
              const t = i / 12;
              g.lineTo(ax - half * 0.62 + t * half * 1.24, seaTop + Math.sin(t * 9.1 + 1.3) * 2.2 + sr() * 2.4);
            }
            for (let i = 1; i <= 5; i++) {
              const t = i / 5;
              g.lineTo(ax + half * (0.62 + 0.38 * t) + (sr() - 0.5) * 20, seaTop + t * deep);
            }
            g.lineTo(ax - half, h);
            for (let i = 5; i >= 1; i--) {
              const t = i / 5;
              g.lineTo(ax - half * (0.62 + 0.38 * t) + (sr() - 0.5) * 20, seaTop + t * deep);
            }
            g.closePath();
            const sea = g.createLinearGradient(0, seaTop, 0, h);
            sea.addColorStop(0, P.skyHorizon);
            sea.addColorStop(0.35, P.cotton);
            sea.addColorStop(1, P.bone);
            g.fillStyle = sea;
            g.globalAlpha = 0.94;
            g.fill();
            g.restore();
            g.globalAlpha = 1;
            /* the water itself is strokes: long broken bone/rose drifts,
               thin and pale at the horizon, wider and looser near */
            for (let i = 0; i < 34; i++) {
              const t = Math.pow(sr(), 1.4);
              const wy = seaTop + 4 + t * (deep - 8);
              const spanW = half * (0.58 + t * 0.4);
              const x0 = ax - spanW + sr() * spanW * 0.75;
              const len = 36 + sr() * spanW * 0.85;
              const pick = sr();
              taper(
                g,
                x0,
                wy,
                Math.min(x0 + len, ax + spanW),
                wy + (sr() - 0.5) * 3,
                1.6 + t * 5 + sr() * 2,
                pick > 0.72 ? P.parchment : pick > 0.38 ? P.bone : pick > 0.16 ? P.skyHorizon : P.rose,
                0.16 + t * 0.28 + sr() * 0.12,
                3
              );
            }
            /* scumbled flanks: ground-toned daubs feather the shoreline
               into the hills — no vertical edge survives */
            for (let e = 0; e < 26; e++) {
              const t = sr();
              const side = e % 2 ? 1 : -1;
              const ex = ax + side * half * (0.62 + 0.38 * t) + (sr() - 0.5) * 20;
              const ey = seaTop + 6 + t * (deep - 12);
              daub(g, ex, ey, 8 + sr() * 14, 3 + sr() * 5, (sr() - 0.5) * 0.5, sr() > 0.5 ? P.bone : sr() > 0.25 ? P.cottonShadow : P.groundShade, 0.22 + sr() * 0.24);
            }
            /* soft horizon: broken bone/rose strokes + a cotton haze so
               the water recedes under the mountains (no ink line) */
            for (let i = 0; i < 8; i++) {
              const hx0 = ax - half * 0.58 + sr() * half;
              taper(g, hx0, seaTop + 1 + sr() * 2.5, hx0 + 26 + sr() * 60, seaTop + 1 + sr() * 3, 1.2 + sr(), sr() > 0.5 ? P.bone : P.rose, 0.18 + sr() * 0.14, 2);
            }
            for (let i = 0; i < 6; i++) {
              const hy = seaTop - 5 + sr() * 16;
              taper(g, ax - half * 0.7 + sr() * 190, hy, ax + sr() * half * 0.65, hy + (sr() - 0.5) * 4, 10 + sr() * 14, P.parchment, 0.16 + sr() * 0.12, 4);
            }
            /* rose glints scattered under the sun path */
            for (let i = 0; i < 6; i++) {
              const wy = seaTop + 8 + sr() * 46;
              taper(g, ax - 40 + sr() * 180, wy, ax + 30 + sr() * 180, wy + (sr() - 0.5) * 2, 1.6 + sr() * 1.4, P.rose, 0.28 + sr() * 0.15, 2);
            }
            /* the moored boat sits IN the water: reflection beneath,
               hull, mast, and waterline strokes lapping OVER the hull */
            const bx = ax - 180;
            const by = seaTop + 62;
            for (let i = 0; i < 5; i++) {
              const ry2 = by + 6 + i * 5 + sr() * 2;
              taper(g, bx - 13 + Math.sin(i * 1.7) * 3 + (sr() - 0.5) * 6, ry2, bx + 12 + (sr() - 0.5) * 8, ry2 + (sr() - 0.5) * 2, 2.6 - i * 0.35, P.umber, 0.26 - i * 0.04, 2);
            }
            taper(g, bx + 2, by + 8, bx + 3.5, by + 26, 1.4, P.noirDeep, 0.14, 3);
            taper(g, bx - 26, by, bx + 26, by + 1, 8, P.umber, 0.92, 4);
            taper(g, bx - 22, by - 5, bx + 20, by - 4, 3.4, P.noirWarm, 0.75, 3);
            taper(g, bx + 2, by - 6, bx + 4, by - 46, 2, P.noirDeep, 0.85, 3);
            taper(g, bx + 4, by - 44, bx + 15, by - 30, 1.2, P.umber, 0.6, 2);
            taper(g, bx - 30, by + 3, bx + 30, by + 4, 3, P.bone, 0.75, 3);
            taper(g, bx - 20, by + 5, bx + 24, by + 5, 2, P.parchment, 0.5, 2);
            daub(g, bx - 28, by + 3, 5, 1.6, 0.1, P.parchment, 0.5);
            daub(g, bx + 27, by + 4, 4.4, 1.4, -0.1, P.bone, 0.55);
          }
        });
        /* distant static figures — cheap painted souls on the hills */
        if (hash(segIndex * 11.3) > 0.5) {
          const fx = w * (0.2 + hash(segIndex * 5.9) * 0.6);
          const fy = h * 0.55 + Math.sin((segIndex * SEG_W + fx) * 0.006 + 1.7) * h * 0.12 + 4;
          taper(g, fx, fy - 9, fx, fy, 3.4, P.umber, 0.6, 3);
          daub(g, fx, fy - 11, 1.7, 2, 0, P.bone, 0.7);
          if (hash(segIndex * 17.1) > 0.5) {
            taper(g, fx + 7, fy - 8, fx + 7, fy + 1, 3, P.umber, 0.5, 3);
            daub(g, fx + 7, fy - 10, 1.5, 1.8, 0, P.bone, 0.6);
          }
        }
      };

      const paintPlay = (g, segIndex, w, h, jitterSeed) => {
        const r = rng(segIndex * 91 + 23 + (jitterSeed || 0));
        const worldX0 = segIndex * SEG_W;
        /* the walkable hill silhouette — parchment/bone warm ground */
        g.fillStyle = P.ground;
        g.beginPath();
        g.moveTo(0, h);
        for (let x = 0; x <= w; x += 16) {
          g.lineTo(x, groundAt(worldX0 + x));
        }
        g.lineTo(w, h);
        g.closePath();
        g.fill();
        /* dry-brush strokes on the slope — warm, no green, no mud */
        for (let i = 0; i < 30; i++) {
          const x = r() * w;
          const gy = groundAt(worldX0 + x);
          taper(g, x, gy + 6 + r() * 30, x + 26 + r() * 40, gy + 8 + r() * 34, 4 + r() * 8, r() > 0.55 ? P.cottonShadow : P.groundShade, 0.55, 4);
        }
        for (let i = 0; i < 9; i++) {
          const x = r() * w;
          const gy = groundAt(worldX0 + x);
          taper(g, x, gy + 10 + r() * 26, x + 40 + r() * 50, gy + 12 + r() * 30, 6 + r() * 9, 'rgba(99,0,0,0.09)', 1, 3);
        }
        /* the crest: a broken noir ink line + a parchment sun-kiss */
        for (let x = 0; x <= w; x += 46) {
          const s0 = hash((worldX0 + x) * 0.117);
          if (s0 < 0.3) continue;
          taper(g, x, groundAt(worldX0 + x) + 1, x + 26 + s0 * 22, groundAt(worldX0 + x + 30) + 1, 1.8, P.noir, 0.18 + s0 * 0.12, 2);
        }
        for (let x = 20; x <= w; x += 64) {
          const s0 = hash((worldX0 + x) * 0.171);
          if (s0 < 0.45) continue;
          taper(g, x, groundAt(worldX0 + x) - 1, x + 30 + s0 * 20, groundAt(worldX0 + x + 34) - 1, 2, P.parchment, 0.4, 2);
        }
        /* grass tufts — dark ink accents, sparse */
        for (let i = 0; i < 16; i++) {
          const x = r() * w;
          const gy = groundAt(worldX0 + x);
          for (let b = 0; b < 3; b++) {
            taper(g, x, gy + 2, x + (r() - 0.5) * 12, gy - 7 - r() * 9, 1.4, r() > 0.5 ? P.umber : P.maroonDark, 0.42, 2);
          }
        }
        /* a few scattered rose blossoms in the open country */
        for (let i = 0; i < 4; i++) {
          const x = r() * w;
          const gy = groundAt(worldX0 + x);
          daub(g, x, gy + 4 + r() * 10, 1.8, 1.2, r(), P.rose, 0.55);
        }
        /* near landmarks (seam rule ±SEAM; furniture stays within ±380) */
        (DATA.areas || []).forEach((area) => {
          if (area.x < worldX0 - SEAM || area.x > worldX0 + w + SEAM) return;
          const ax = area.x - worldX0;
          const gy = groundAt(area.x);
          if (area.kind === 'temple') {
            temple(g, ax, gy, 260, 190, 6, Math.round(area.x) + 4, false);
          } else if (area.kind === 'ruin') {
            /* dark cypress masses FIRST — the noir-umber backdrop that
               lets the bone marble read against the light hills */
            cypress(g, ax - 64, groundAt(area.x - 64), 132, Math.round(area.x) + 9, false);
            cypress(g, ax - 126, groundAt(area.x - 126), 96, Math.round(area.x) + 10, false);
            cypress(g, ax + 92, groundAt(area.x + 92), 112, Math.round(area.x) + 11, false);
            temple(g, ax, gy, 200, 150, 5, Math.round(area.x) + 6, true);
            /* a lone standing column west of the ruin — the owl's perch,
               grounded with a contact shadow, carved out of the cypress */
            const cx = ax - 90;
            const cgy = groundAt(area.x - 90);
            g.save();
            g.globalAlpha = 0.18;
            g.fillStyle = P.noir;
            g.beginPath();
            g.ellipse(cx, cgy, 13, 3, 0, 0, TAU);
            g.fill();
            g.restore();
            for (let d = 0; d < 5; d++) {
              g.fillStyle = d % 2 ? P.bone : P.cotton;
              g.globalAlpha = 0.95;
              g.fillRect(cx - 6, cgy - 18 - d * 17, 12, 16);
            }
            g.globalAlpha = 1;
            taper(g, cx + 6, cgy - 98, cx + 6, cgy - 4, 1.1, P.noir, 0.3, 3);
            g.fillStyle = P.cotton;
            g.fillRect(cx - 8, cgy - 104, 16, 5);
          } else if (area.kind === 'gate') {
            cypress(g, ax - 60, gy, 150, Math.round(area.x) * 8 + 1, false);
            cypress(g, ax + 60, gy, 170, Math.round(area.x) * 8 + 2, false);
          } else if (area.kind === 'harbor') {
            /* seeded from the landmark (+ boil jitter, shared by both
               seam neighbours) so the quay is one painting at the seam */
            const hr = rng(Math.round(area.x) * 3 + 2 + (jitterSeed || 0));
            /* the shoreline: the bay's near edge laps against the crest —
               irregular feathered water strokes riding where the ground
               dips, so the sea starts AT the shore, never above it */
            for (let i = 0; i < 24; i++) {
              const x = ax - 110 + hr() * 480;
              const gy2 = groundAt(worldX0 + x);
              const pick = hr();
              taper(g, x, gy2 - 1 - hr() * 2.5, x + 14 + hr() * 30, gy2 - 1 + (hr() - 0.5) * 2.5, 1.6 + hr() * 2.6, pick > 0.6 ? P.skyHorizon : pick > 0.3 ? P.bone : P.rose, 0.18 + hr() * 0.22, 2);
            }
            /* wet-sand sheen just under the crest */
            for (let i = 0; i < 8; i++) {
              const x = ax - 200 + hr() * 560;
              const gy2 = groundAt(worldX0 + x);
              taper(g, x, gy2 + 3, x + 24 + hr() * 30, gy2 + 4, 2.5 + hr() * 2, P.skyHorizon, 0.14 + hr() * 0.1, 2);
            }
            /* the quay: warm sand, bone stone edge, harbor clutter */
            for (let i = 0; i < 14; i++) {
              const x = ax - 340 + hr() * 680;
              const gy2 = groundAt(worldX0 + x);
              taper(g, x, gy2 + 4 + hr() * 14, x + 20 + hr() * 26, gy2 + 6 + hr() * 16, 3 + hr() * 5, hr() > 0.5 ? P.cottonShadow : P.parchment, 0.5, 3);
            }
            /* quay slabs west toward the water */
            for (let q = 0; q < 5; q++) {
              const qx = ax - 330 + q * 34;
              const qy = groundAt(worldX0 + qx);
              g.fillStyle = q % 2 ? P.bone : P.cottonShadow;
              g.globalAlpha = 0.9;
              g.fillRect(qx, qy - 2, 30, 7);
              g.globalAlpha = 1;
              taper(g, qx, qy + 5, qx + 30, qy + 5, 1, P.noir, 0.2, 2);
            }
            /* bollard + coiled rope + amphorae */
            const bx = ax - 190;
            const bgy = groundAt(area.x - 190);
            taper(g, bx, bgy, bx, bgy - 14, 6, P.umber, 0.9, 3);
            g.strokeStyle = P.umber;
            g.globalAlpha = 0.75;
            g.lineWidth = 2;
            g.beginPath();
            g.arc(bx + 22, bgy - 2, 6, 0, TAU);
            g.arc(bx + 22, bgy - 2, 3, 0, TAU);
            g.stroke();
            g.globalAlpha = 1;
            const jx = ax + 230;
            const jgy = groundAt(area.x + 230);
            daub(g, jx, jgy - 8, 6, 9, 0, P.umber, 0.9);
            daub(g, jx + 14, jgy - 7, 5, 8, 0.1, P.maroonDark, 0.85);
            taper(g, jx - 3, jgy - 16, jx + 3, jgy - 16, 3, P.umber, 0.9, 2);
          } else if (area.kind === 'amphitheatre') {
            /* a warm flat stone west (the lizard's sunning slab) and
               goat rocks east */
            const lx = ax - 260;
            const lgy = groundAt(area.x - 260);
            daub(g, lx, lgy - 2, 26, 7, 0.05, P.bone, 0.9);
            taper(g, lx - 20, lgy + 4, lx + 22, lgy + 5, 1.4, P.noir, 0.25, 3);
            const rx2 = ax + 240;
            const rgy = groundAt(area.x + 240);
            daub(g, rx2, rgy - 8, 24, 14, 0, P.warmGreyLight, 0.9);
            daub(g, rx2 + 34, rgy - 5, 16, 10, 0.2, P.bone, 0.9);
            daub(g, rx2 - 28, rgy - 4, 13, 8, -0.15, P.warmGrey, 0.85);
            taper(g, rx2 - 40, rgy + 3, rx2 + 46, rgy + 5, 1.6, P.noir, 0.22, 3);
          } else if (area.kind === 'statues') {
            /* the garden floor: rose blossom drifts for the butterflies —
               landmark-seeded (+ boil jitter) for seam-identical strokes */
            const tr = rng(Math.round(area.x) * 3 + 4 + (jitterSeed || 0));
            for (let i = 0; i < 12; i++) {
              const x = ax - 150 + tr() * 300;
              const gy2 = groundAt(worldX0 + x);
              daub(g, x, gy2 + 3 + tr() * 8, 2 + tr(), 1.2, tr() * 2, tr() > 0.35 ? P.rose : P.bone, 0.6);
              taper(g, x, gy2 + 5, x + (tr() - 0.5) * 6, gy2 - 3, 1, P.warmGrey, 0.5, 2);
            }
          } else if (area.kind === 'grove') {
            /* fallen olives + a ladder against the far trunk —
               landmark-seeded (+ boil jitter) for seam-identical strokes */
            const or2 = rng(Math.round(area.x) * 3 + 1 + (jitterSeed || 0));
            for (let i = 0; i < 8; i++) {
              const x = ax - 120 + or2() * 240;
              const gy2 = groundAt(worldX0 + x);
              daub(g, x, gy2 + 4 + or2() * 8, 1.4, 1, or2(), P.umber, 0.5);
            }
          }
        });
      };

      /* ---------- Segment LRU per plane (negative indices are normal).
         Bound sized from the viewport in fit(): the boil alternates two
         cache keys on the near plane, so keep two full visible spans
         (9 as the floor for narrow viewports). */
      let maxSegEntries = 9;
      const makePlane = (painter, factor, heightF) => ({
        painter,
        factor,
        heightF,
        cache: new Map(), // segIndex -> canvas
        get(segIndex, jitterSeed) {
          const key = segIndex + ':' + (jitterSeed || 0);
          if (this.cache.has(key)) {
            const hit = this.cache.get(key);
            this.cache.delete(key);
            this.cache.set(key, hit);
            return hit;
          }
          const c = document.createElement('canvas');
          const h = Math.round(H * this.heightF);
          c.width = Math.round(SEG_W * dpr);
          c.height = Math.round(h * dpr);
          const g = c.getContext('2d');
          g.setTransform(dpr, 0, 0, dpr, 0, 0);
          this.painter(g, segIndex, SEG_W, h, jitterSeed);
          this.cache.set(key, c);
          while (this.cache.size > maxSegEntries) {
            const first = this.cache.keys().next().value;
            this.cache.delete(first);
          }
          return c;
        },
        clear() {
          this.cache.clear();
        },
      });

      let skyCanvas = null;
      const farPlane = makePlane(paintFar, 0.16, 1);
      const midPlane = makePlane(paintMid, 0.45, 1);
      const playPlane = makePlane(paintPlay, 1, 1);

      let grainPat = null;
      const makeGrain = () => {
        const c = document.createElement('canvas');
        c.width = 96;
        c.height = 96;
        const g = c.getContext('2d');
        const img = g.createImageData(96, 96);
        for (let i = 0; i < img.data.length; i += 4) {
          const v = 128 + Math.round((hash(i * 0.31) - 0.5) * 66);
          img.data[i] = v;
          img.data[i + 1] = v;
          img.data[i + 2] = v;
          img.data[i + 3] = 255;
        }
        g.putImageData(img, 0, 0);
        grainPat = ctx.createPattern(c, 'repeat');
      };

      const fit = () => {
        const rect = stage.getBoundingClientRect();
        /* DPR caps per the master contract: ≤1.5 desktop, ≤1.25 ≤768px */
        const narrow = window.matchMedia('(max-width: 768px)').matches;
        dpr = Math.min(window.devicePixelRatio || 1, narrow ? 1.25 : 1.5);
        W = Math.max(1, Math.round(rect.width));
        H = Math.max(1, Math.round(rect.height));
        canvas.width = Math.round(W * dpr);
        canvas.height = Math.round(H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        groundBase = H * 0.8;
        maxSegEntries = Math.max(9, 2 * (Math.ceil((W + SEG_W) / SEG_W) + 2));
        skyCanvas = null;
        farPlane.clear();
        midPlane.clear();
        playPlane.clear();
        if (!grainPat) makeGrain();
      };
      fit();

      /* ---------- The wanderer (a travelling painter) */
      const player = {
        x: DATA.startX || 400,
        vx: 0,
        facing: 1,
        step: 0,
      };
      const SPEED = 220;

      const drawPlayer = (g, sx, sy, t) => {
        const moving = Math.abs(player.vx) > 4;
        const bob = Math.sin(player.step * 9) * (moving ? 2.4 : 0.7);
        charBackdrop(g, sx, sy, 40, 0.85);
        if (sx > 50 && sx < W - 50) {
          figRects.push({
            name: 'The Traveller',
            x: Math.round(sx - 17),
            y: Math.round(sy + bob - 68),
            w: 34,
            h: 70,
            interactive: false,
          });
        }
        g.save();
        g.translate(sx, sy + bob);
        g.save();
        g.scale(player.facing, 1);
        /* legs — two alternating strokes */
        const lpp = Math.sin(player.step * 9) * (moving ? 6 : 0.5);
        taper(g, -3, -8, -3 - lpp, 2, 6, P.noirDeep, 1, 3);
        taper(g, 4, -8, 4 + lpp, 2, 6, P.noirDeep, 1, 3);
        /* cloak — noir-warm under maroon: dark against the day ground */
        taper(g, 0, -52, 0, -6, 26, P.noirWarm, 0.97, 6);
        taper(g, -3, -50, 2, -8, 16, P.maroon, 0.6, 5);
        taper(g, -8, -46, -7, -12, 2.2, P.cotton, 0.5, 4); // cotton trim edge
        /* head + warm face light from the sun */
        daub(g, 1, -60, 7.5, 8.5, 0, P.bone, 0.98);
        daub(g, 3.5, -61, 3.2, 4, 0, P.rose, 0.45);
        taper(g, -5, -66, 6, -64, 5.5, P.noir, 0.9, 3); // hair
        /* the unfinished canvas he carries */
        g.fillStyle = P.parchment;
        g.globalAlpha = 0.96;
        g.fillRect(8, -44, 15, 20);
        g.globalAlpha = 1;
        g.strokeStyle = P.umber;
        g.lineWidth = 1.4;
        g.strokeRect(8, -44, 15, 20);
        taper(g, 10, -36, 20, -33, 1.6, P.cherry, 0.7, 2); // his small red mark
        g.restore();
        /* rim light, sun side */
        taper(g, 9, -48, 10.5, -12, 2.2, P.parchment, 0.5, 4);
        g.restore();
      };

      const drawNpc = (g, npc, sx, sy, t) => {
        drawFigure(g, {
          name: npc.name,
          interactive: true,
          seed: npc.seed,
          x: sx,
          y: sy,
          sway: t ? Math.sin(t * 0.8 + npc.seed * 37) * 1.2 : 0,
          face: player.x >= npc.x ? 1 : -1,
          robe: npc.tone === 'light' ? 'light' : 'dark',
          attr: npc.attr,
        });
      };

      /* ---------- Camera (Keren: dead zone + damped look-ahead) */
      let camX = clamp(player.x, W * 0.5, Math.max(WORLD_W - W * 0.5, W * 0.5));
      let lookAhead = 0;

      const worldToScreen = (wx) => wx - camX + W * 0.5;

      /* =================================================================
         THE LIVING CAST — code-driven ambient figures & animals.
         Structured config, deterministic seeds, proximity reactions.
         ================================================================= */
      const areaBy = (kind) => (DATA.areas || []).find((a) => a.kind === kind) || null;
      const AREA = {
        harbor: areaBy('harbor') || areaBy('gate'),
        grove: areaBy('grove'),
        amphi: areaBy('amphitheatre'),
        temple: areaBy('temple'),
        garden: areaBy('statues'),
        sanctuary: areaBy('ruin'),
      };

      const AMBIENT = [];
      const ANIMALS = [];

      /* -- ambient humans ------------------------------------------- */

      const makeSailors = (wx, seed) => ({
        kind: 'sailors',
        name: 'A sailor',
        x: wx,
        barks: ['Heave. Again. The rope respects rhythm.', 'Wind is fair for the far shore today.'],
        t: hash(seed) * 9,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x);
          const pull = Math.sin(this.t * 1.7);
          drawFigure(g, { name: 'A sailor', x: sx, y: gy, s: 0.92, face: -1, robe: 'dark', hazy: 0.35, pose: 'haul', gest: pull * 0.5 + 0.5, lean: 0.13 + pull * 0.04 });
          drawFigure(g, { name: 'A sailor', x: sx + 34, y: groundAt(this.x + 34), s: 0.9, face: -1, robe: 'dark', hazy: 0.42, pose: 'haul', gest: 0.5 - pull * 0.5, lean: 0.11 - pull * 0.04 });
          /* the rope, taut toward the bollard on the quay */
          taper(g, sx - 13, gy - 29, sx - 58, gy - 6, 2.2, P.umber, 0.65, 4);
        },
      });

      const makeNymph = (wx, seed) => ({
        kind: 'nymph',
        name: 'A nymph of the shallows',
        x: wx,
        barks: ['The water keeps what the land forgets.', 'Even the sea paints in strokes. Watch the light.'],
        t: hash(seed) * 7,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x);
          const sw = Math.sin(this.t * 0.9) * 2.4;
          /* ethereal but READABLE: rose-wash robe, noir hair line (the
             rose robe keeps the dark hair branch), faint maroon contour */
          drawFigure(g, { name: 'A nymph', x: sx, y: gy, s: 0.95, face: 1, robe: 'rose', hazy: 0.18, alpha: 0.88, sway: sw });
          taper(g, sx + sw - 11, gy - 46, sx - 8, gy - 8, 1.4, P.maroon, 0.4, 4);
          taper(g, sx + sw + 10, gy - 48, sx + 9, gy - 10, 1.1, P.maroon, 0.3, 4);
          daub(g, sx + 4, gy - 10, 5, 2, 0, P.rose, 0.35);
        },
      });

      const makeWorker = (wx, pose, name, barks, seed) => ({
        kind: 'worker',
        name,
        x: wx,
        barks,
        t: hash(seed) * 6,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x);
          drawFigure(g, {
            name,
            x: sx,
            y: gy,
            s: 0.94,
            face: pose === 'pick' ? 1 : -1,
            robe: 'dark',
            hazy: 0.38,
            pose,
            phase: pose === 'pick' ? this.t * 0.9 : 0,
            lean: pose === 'stoop' ? 0.3 : 0,
          });
          /* the basket at their feet */
          daub(g, sx + (pose === 'pick' ? -12 : 10), gy - 4, 7, 4, 0, P.umber, 0.8);
          taper(g, sx + (pose === 'pick' ? -18 : 4), gy - 8, sx + (pose === 'pick' ? -6 : 16), gy - 8, 1.6, P.bone, 0.6, 2);
        },
      });

      const makeMusician = (wx, seed) => ({
        kind: 'musician',
        name: 'A musician',
        x: wx,
        barks: ['The stones remember every note.', 'Dorian, always Dorian — the hills prefer it.'],
        t: hash(seed) * 5,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          drawFigure(g, {
            name: 'A musician',
            x: worldToScreen(this.x),
            y: groundAt(this.x),
            s: 0.95,
            face: 1,
            robe: 'dark',
            hazy: 0.34,
            pose: 'strum',
            attr: 'lyre',
            sway: Math.sin(this.t * 1.15) * 1.6,
          });
        },
      });

      const makePair = (wx, name, seed) => ({
        kind: 'pair',
        name,
        x: wx,
        barks: [],
        t: hash(seed) * 8,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const gA = Math.max(0, Math.sin(this.t * 0.5));
          const gB = Math.max(0, Math.sin(this.t * 0.5 + 2.4));
          drawFigure(g, { name, x: worldToScreen(this.x), y: groundAt(this.x), s: 0.92, face: 1, robe: 'dark', hazy: 0.45, pose: 'gesture', gest: gA, sway: Math.sin(this.t * 0.7) * 0.8 });
          drawFigure(g, { name, x: worldToScreen(this.x + 27), y: groundAt(this.x + 27), s: 0.9, face: -1, robe: 'dark', hazy: 0.5, pose: 'gesture', gest: gB, sway: Math.sin(this.t * 0.7 + 1.4) * 0.8 });
        },
      });

      const makeArtisan = (wx, seed) => ({
        kind: 'artisan',
        name: 'The marble artisan',
        x: wx,
        barks: ['Chip by chip. That is the whole secret.', 'The figure is already inside. I only subtract.'],
        t: hash(seed) * 4,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x);
          /* the half-carved block */
          g.fillStyle = P.bone;
          g.globalAlpha = 0.95;
          g.fillRect(sx + 14, gy - 20, 22, 20);
          g.globalAlpha = 1;
          daub(g, sx + 25, gy - 24, 7, 6, 0, P.cotton, 0.9);
          taper(g, sx + 14, gy - 20, sx + 36, gy - 19, 1.2, P.noir, 0.3, 3);
          taper(g, sx + 36, gy - 20, sx + 36, gy, 1, P.noir, 0.25, 3);
          const working = this.t % 9 < 6;
          const k = working ? Math.abs(Math.sin(this.t * 6)) : 0;
          drawFigure(g, { name: 'The marble artisan', x: sx, y: gy, s: 0.95, face: 1, robe: 'dark', hazy: 0.32, pose: 'chisel', strike: k });
        },
      });

      const makeStatueAmb = (wx, seed) => {
        /* the dark laurel mass behind the marble is fixed at build time
           (deterministic, cheap to redraw each frame) */
        const fr = rng(seed * 5 + 1);
        const leaves = [];
        for (let i = 0; i < 12; i++) {
          leaves.push({
            dx: (fr() - 0.5) * 38,
            dy: -24 - fr() * 32,
            rx: 6 + fr() * 8,
            ry: 4 + fr() * 4.5,
            a: (fr() - 0.5) * 0.9,
            c: fr(),
          });
        }
        return {
          kind: 'statue',
          name: 'A statue',
          x: wx,
          barks: ['You saw nothing.'],
          t: 0,
          tt: 0,
          turning: false,
          nextTurn: 18 + hash(seed) * 22,
          onscreen: false,
          update(dt, pd) {
            this.t += dt;
            if (!this.turning && this.t > this.nextTurn) {
              this.turning = true;
              this.tt = 0;
            }
            if (this.turning) {
              this.tt += dt;
              if (this.tt > 4) {
                this.turning = false;
                this.t = 0;
                this.nextTurn = 20 + hash(seed + this.tt) * 24;
              }
            }
          },
          draw(g, t) {
            const sx = worldToScreen(this.x);
            const gy = groundAt(this.x);
            const headT = this.turning ? Math.sin(Math.min(this.tt / 4, 1) * Math.PI) : 0;
            const dir = player.x >= this.x ? 1 : -1;
            /* dark separation backdrop — bone never sits on bone */
            for (let i = 0; i < leaves.length; i++) {
              const L = leaves[i];
              daub(g, sx + L.dx, gy + L.dy, L.rx, L.ry, L.a, L.c > 0.62 ? P.warmGrey : L.c > 0.28 ? P.umber : P.noirDeep, 0.5 + L.c * 0.3);
            }
            taper(g, sx - 2, gy - 2, sx - 5, gy - 52, 6.5, P.noirDeep, 0.5, 4);
            /* grounded contact shadow */
            g.save();
            g.globalAlpha = 0.24;
            g.fillStyle = P.noir;
            g.beginPath();
            g.ellipse(sx, gy + 1, 14, 3.2, 0, 0, TAU);
            g.fill();
            g.restore();
            if (sx > 50 && sx < W - 50) {
              figRects.push({ name: 'A statue', x: Math.round(sx - 14), y: Math.round(gy - 52), w: 28, h: 54, interactive: false });
            }
            g.fillStyle = P.umber;
            g.fillRect(sx - 11, gy - 9, 22, 9);
            taper(g, sx - 11, gy - 9, sx + 11, gy - 9, 1.2, P.noir, 0.35, 2);
            taper(g, sx, gy - 30, sx, gy - 9, 15, P.bone, 0.92, 5);
            taper(g, sx - 4, gy - 28, sx + 2, gy - 13, 2.4, P.cottonShadow, 0.7, 4);
            /* stronger noir ink contour, both flanks + the head */
            taper(g, sx + 6, gy - 32, sx + 6.5, gy - 11, 1.7, P.noir, 0.55, 3);
            taper(g, sx - 6.5, gy - 31, sx - 6, gy - 11, 1.2, P.noir, 0.4, 3);
            daub(g, sx + headT * dir * 3.2, gy - 37, 5.6, 6.4, headT * dir * 0.12, P.bone, 0.94);
            g.strokeStyle = P.noir;
            g.globalAlpha = 0.42;
            g.lineWidth = 1.1;
            g.beginPath();
            g.arc(sx + headT * dir * 3.2, gy - 37, 5.9, -0.7, 1.9);
            g.stroke();
            g.globalAlpha = 1;
            /* rim light so the marble reads against the laurel dark */
            taper(g, sx + 7, gy - 34, sx + 8, gy - 12, 1.8, P.parchment, 0.65, 3);
          },
        };
      };

      const makeTraveller = (from, to, speed, startT, name, seed) => ({
        kind: 'traveller',
        name,
        x: lerp(from, to, startT),
        from,
        to,
        dir: 1,
        speed,
        barks: ['A fine day for the coast road.', 'The temple is worth the walk, friend.'],
        phase: 0,
        pauseT: 0,
        lastStop: null,
        onscreen: false,
        update(dt) {
          if (this.pauseT > 0) {
            this.pauseT -= dt;
            return;
          }
          this.x += this.dir * this.speed * dt;
          this.phase += dt * (this.speed / 40);
          if (this.x >= this.to) {
            this.x = this.to;
            this.dir = -1;
            this.lastStop = null;
          } else if (this.x <= this.from) {
            this.x = this.from;
            this.dir = 1;
            this.lastStop = null;
          }
          for (let i = 0; i < (DATA.areas || []).length; i++) {
            const a = DATA.areas[i];
            if (a !== this.lastStop && Math.abs(a.x - this.x) < 26) {
              this.lastStop = a;
              this.pauseT = 3.5 + hash(this.x * 0.13 + seed) * 5;
              break;
            }
          }
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x);
          drawFigure(g, {
            name: this.name,
            x: sx,
            y: gy,
            s: 0.94,
            face: this.dir,
            robe: 'dark',
            hazy: 0.4,
            phase: this.pauseT > 0 ? 0 : this.phase,
            sway: this.pauseT > 0 && t ? Math.sin(t * 0.8 + seed) * 1.1 : 0,
          });
          /* bundle on a stick over the shoulder */
          daub(g, sx - this.dir * 9, gy - 54, 4, 3, 0.4, P.umber, 0.65);
        },
      });

      /* -- animals --------------------------------------------------- */

      const makeDoves = (wx, n, seed) => {
        const r = rng(seed);
        const birds = [];
        for (let i = 0; i < n; i++) birds.push({ dx: (r() - 0.5) * 90, a: r() * TAU });
        return {
          kind: 'doves',
          x: wx,
          layer: 'air',
          state: 'ground',
          t: 0,
          rt: 0,
          onscreen: false,
          update(dt, pd) {
            this.t += dt;
            if (this.state === 'ground' && pd < 150) {
              this.state = 'air';
              this.t = 0;
              if (window.AtelierAudio && window.AtelierAudio.event) window.AtelierAudio.event('bird');
            } else if (this.state === 'air' && this.t > 5 && pd > 380) {
              this.state = 'return';
              this.rt = 0;
            } else if (this.state === 'return') {
              this.rt += dt;
              if (this.rt > 1.4) this.state = 'ground';
            }
          },
          draw(g, t) {
            const gy = groundAt(this.x);
            const sx = worldToScreen(this.x);
            for (let i = 0; i < birds.length; i++) {
              const b = birds[i];
              let bx;
              let by;
              let up = 0;
              if (this.state === 'ground') {
                bx = b.dx;
                by = -2 + (t ? Math.sin(t * 2.1 + b.a) * 0.8 : 0);
              } else {
                up = this.state === 'air' ? Math.min(this.t * 1.4, 1) : Math.max(1 - this.rt / 1.4, 0);
                const ang = (t || 1) * 0.9 + b.a;
                bx = b.dx * (1 + up * 0.8) + Math.cos(ang) * 62 * up;
                by = -2 - up * (82 + i * 7) + Math.sin(ang * 1.4) * 9 * up;
              }
              const flap = up > 0.05 ? Math.sin((t || 1) * 16 + b.a) : 0;
              daub(g, sx + bx, gy + by - 3, 3.4, 2.3, 0, P.bone, 0.95);
              taper(g, sx + bx - 1, gy + by - 4, sx + bx - 6, gy + by - 6 - flap * 4, 1.6, P.noir, 0.75, 2);
              taper(g, sx + bx + 1, gy + by - 4, sx + bx + 6, gy + by - 6 + flap * 4, 1.6, P.noir, 0.75, 2);
              if (up <= 0.05) daub(g, sx + bx + 2.6, gy + by - 4.8, 1.3, 1.3, 0, P.bone, 0.9);
            }
          },
        };
      };

      const makeGoats = (wx, seed) => {
        const r = rng(seed);
        const goats = [{ o: -6, p: r() }, { o: 46, p: r() }];
        return {
          kind: 'goats',
          x: wx,
          layer: 'ground',
          t: 0,
          onscreen: false,
          update(dt) {
            this.t += dt;
          },
          draw(g, t) {
            const sx = worldToScreen(this.x);
            for (let i = 0; i < goats.length; i++) {
              const gt = goats[i];
              const wob = this.t * (0.11 + i * 0.03) + gt.p * 9;
              const px = gt.o + Math.sin(wob) * 52;
              const graze = Math.sin(this.t * 0.23 + gt.p * 7) > 0.55;
              const gy = groundAt(this.x + px) - 8;
              const dir = Math.cos(wob) >= 0 ? 1 : -1;
              daub(g, sx + px, gy - 9, 8.5, 5, 0, P.umber, 0.95);
              taper(g, sx + px - 5, gy - 6, sx + px - 5, gy, 1.8, P.noirDeep, 0.9, 2);
              taper(g, sx + px + 5, gy - 6, sx + px + 5, gy, 1.8, P.noirDeep, 0.9, 2);
              const hx = sx + px + dir * 9;
              const hy = graze ? gy - 3 : gy - 13;
              daub(g, hx, hy, 3, 2.4, 0, P.umber, 0.95);
              taper(g, hx, hy - 2, hx - dir * 3, hy - 6, 1.2, P.noir, 0.9, 2);
              taper(g, hx + dir, hy - 2, hx - dir, hy - 7, 1.2, P.noir, 0.9, 2);
            }
          },
        };
      };

      const makeSheep = (wx, seed) => {
        const r = rng(seed);
        const flock = [{ o: -26, p: r() }, { o: 6, p: r() }, { o: 34, p: r() }];
        return {
          kind: 'sheep',
          x: wx,
          layer: 'ground',
          t: 0,
          alert: false,
          onscreen: false,
          update(dt, pd) {
            this.t += dt;
            this.alert = pd < 160;
          },
          draw(g, t) {
            const sx = worldToScreen(this.x);
            for (let i = 0; i < flock.length; i++) {
              const sh = flock[i];
              const px = sh.o + Math.sin(this.t * 0.05 + sh.p * 9) * 12;
              const gy = groundAt(this.x + px);
              const bob = this.alert ? 0 : Math.sin(this.t * 1.1 + sh.p * 5) * 0.7;
              daub(g, sx + px, gy - 8 + bob, 9, 6, 0, P.bone, 0.96);
              daub(g, sx + px - 2, gy - 10 + bob, 6, 4, 0.3, P.parchment, 0.7);
              taper(g, sx + px - 4, gy - 4, sx + px - 4, gy, 1.4, P.noir, 0.85, 2);
              taper(g, sx + px + 4, gy - 4, sx + px + 4, gy, 1.4, P.noir, 0.85, 2);
              const hy = this.alert ? gy - 13 : gy - 6 + bob;
              const dir = player.x >= this.x + px ? 1 : -1;
              daub(g, sx + px + (this.alert ? dir * 6 : 8), hy, 2.6, 2, 0, P.noirWarm, 0.95);
            }
          },
        };
      };

      const makeCat = (wx, seed) => ({
        kind: 'cat',
        x: wx,
        layer: 'ground',
        state: 'rest',
        t: 0,
        sitT: 0,
        onscreen: false,
        update(dt, pd) {
          this.t += dt;
          if (this.state === 'rest' && pd < 120) {
            this.state = 'stretch';
            this.t = 0;
          } else if (this.state === 'stretch' && this.t > 1.1) {
            this.state = 'sit';
            this.sitT = 0;
          } else if (this.state === 'sit') {
            this.sitT += dt;
            if (this.sitT > 6 && pd > 260) this.state = 'rest';
          }
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x) - 9; /* on the temple step */
          if (this.state === 'rest') {
            daub(g, sx, gy - 4, 8, 4.6, 0, P.noirWarm, 0.95);
            daub(g, sx + 6, gy - 6, 3, 2.6, 0, P.noirWarm, 0.95);
            g.strokeStyle = P.noirWarm;
            g.globalAlpha = 0.9;
            g.lineWidth = 1.8;
            g.beginPath();
            g.arc(sx - 4, gy - 3, 5, Math.PI * 0.2, Math.PI * 1.1);
            g.stroke();
            g.globalAlpha = 1;
          } else if (this.state === 'stretch') {
            const k = Math.sin(Math.min(this.t / 1.1, 1) * Math.PI);
            taper(g, sx - 8, gy, sx + 8, gy - 6 - k * 3, 5.5, P.noirWarm, 0.95, 4);
            taper(g, sx - 8, gy, sx - 10, gy + 1, 2, P.noirWarm, 0.9, 2);
            daub(g, sx + 10, gy - 8 - k * 3, 2.8, 2.4, 0, P.noirWarm, 0.95);
            taper(g, sx - 9, gy - 2, sx - 15, gy - 9 - k * 2, 1.4, P.noirWarm, 0.9, 3);
          } else {
            daub(g, sx, gy - 6, 4.4, 6.6, 0, P.noirWarm, 0.95);
            daub(g, sx + 1, gy - 13, 3, 2.8, 0, P.noirWarm, 0.95);
            taper(g, sx - 1, gy - 15, sx - 2.6, gy - 17.6, 1.1, P.noirWarm, 0.95, 2);
            taper(g, sx + 3, gy - 15, sx + 4.6, gy - 17.6, 1.1, P.noirWarm, 0.95, 2);
            taper(g, sx + 4, gy - 2, sx + 9, gy - 6, 1.4, P.noirWarm, 0.9, 3);
            daub(g, sx, gy - 8, 2, 2.6, 0, P.bone, 0.5); /* chest light */
          }
        },
      });

      const makeOwl = (wx, seed) => ({
        kind: 'owl',
        x: wx,
        layer: 'ground',
        t: hash(seed) * 3,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const py = groundAt(this.x) - 102; /* atop the lone column */
          daub(g, sx, py - 7, 6.4, 8.8, 0, P.umber, 0.96);
          daub(g, sx, py - 12, 5, 5.4, 0, P.bone, 0.85);
          taper(g, sx - 4, py - 17, sx - 2.4, py - 14, 1.2, P.umber, 0.9, 2);
          taper(g, sx + 4, py - 17, sx + 2.4, py - 14, 1.2, P.umber, 0.9, 2);
          /* the head tracks the wanderer */
          const ex = clamp((player.x - this.x) / 90, -1, 1) * 2;
          const blink = Math.sin(this.t * 0.47) > 0.985;
          if (blink) {
            taper(g, sx - 2.6 + ex, py - 12, sx - 0.6 + ex, py - 12, 1, P.noir, 0.9, 1);
            taper(g, sx + 0.6 + ex, py - 12, sx + 2.6 + ex, py - 12, 1, P.noir, 0.9, 1);
          } else {
            daub(g, sx - 1.7 + ex, py - 12, 1.2, 1.4, 0, P.noirDeep, 0.95);
            daub(g, sx + 1.7 + ex, py - 12, 1.2, 1.4, 0, P.noirDeep, 0.95);
          }
          daub(g, sx + ex * 0.5, py - 10.4, 0.8, 1, 0, P.umber, 0.9);
        },
      });

      const makeButterfly = (wx, seed) => ({
        kind: 'butterfly',
        x: wx,
        layer: 'air',
        t: hash(seed) * 12,
        ph: hash(seed * 3.1) * TAU,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x) + Math.sin(this.t * 0.6 + this.ph) * 26;
          const sy = groundAt(this.x) - 24 - Math.sin(this.t * 0.9 + this.ph * 2) * 12;
          const flut = Math.sin(this.t * 11 + this.ph);
          const wing = 0.4 + Math.abs(flut) * 0.6;
          daub(g, sx - 2.4 * wing, sy - 1, 2.6 * wing, 1.8, -0.4 * flut, P.rose, 0.85);
          daub(g, sx + 2.4 * wing, sy - 1, 2.6 * wing, 1.8, 0.4 * flut, P.bone, 0.85);
          daub(g, sx, sy, 0.8, 1.6, 0, P.noir, 0.9);
        },
      });

      const makeBees = (wx, seed) => ({
        kind: 'bees',
        x: wx,
        layer: 'air',
        t: hash(seed) * 8,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x);
          const gy = groundAt(this.x);
          for (let i = 0; i < 3; i++) {
            const bx = sx + Math.sin(this.t * 3.1 + i * 2.1) * 9 + i * 6;
            const by = gy - 15 - Math.cos(this.t * 2.7 + i * 1.7) * 7;
            daub(g, bx, by, 1.2, 0.9, 0, P.umber, 0.85);
            if (Math.sin(this.t * 21 + i) > 0) daub(g, bx, by - 1.4, 1, 0.5, 0.3, P.bone, 0.6);
          }
        },
      });

      const makeLizard = (wx, seed) => ({
        kind: 'lizard',
        x: wx,
        layer: 'ground',
        state: 'bask',
        t: hash(seed) * 5,
        px: 0,
        dartT: 0,
        onscreen: false,
        update(dt, pd) {
          this.t += dt;
          if (this.state === 'bask' && pd < 90) {
            this.state = 'dart';
            this.dartT = 0;
          } else if (this.state === 'dart') {
            this.dartT += dt;
            this.px += 300 * dt;
            if (this.dartT > 0.45) this.state = 'hidden';
          } else if (this.state === 'hidden' && pd > 400) {
            this.state = 'bask';
            this.px = 0;
          }
        },
        draw(g, t) {
          if (this.state === 'hidden') return;
          const sx = worldToScreen(this.x) + this.px;
          const gy = groundAt(this.x) - 6; /* on the warm slab */
          const a = this.state === 'dart' ? Math.max(1 - this.dartT / 0.45, 0) : 1;
          const tw = this.state === 'bask' && Math.sin(this.t * 0.5) > 0.96 ? Math.sin(this.t * 30) * 1.5 : 0;
          taper(g, sx - 5, gy, sx + 5, gy, 2.4, P.umber, 0.9 * a, 3);
          taper(g, sx - 5, gy, sx - 11, gy + 1 + tw, 1.2, P.umber, 0.85 * a, 3);
          daub(g, sx + 6.4, gy - 0.4, 1.6, 1.2, 0, P.noirWarm, 0.9 * a);
          daub(g, sx - 2, gy + 1.6, 0.8, 0.6, 0, P.noirDeep, 0.8 * a);
          daub(g, sx + 3, gy + 1.6, 0.8, 0.6, 0, P.noirDeep, 0.8 * a);
        },
      });

      const makeTortoise = (wx, seed) => ({
        kind: 'tortoise',
        x: wx,
        layer: 'ground',
        t: hash(seed) * 20,
        px: (hash(seed * 1.7) - 0.5) * 120,
        dir: hash(seed * 2.3) > 0.5 ? 1 : -1,
        onscreen: false,
        update(dt) {
          this.t += dt;
          const moving = Math.sin(this.t * 0.12) > 0.15;
          if (moving) {
            this.px += this.dir * 3.5 * dt;
            if (this.px > 90) this.dir = -1;
            if (this.px < -90) this.dir = 1;
          }
          this.moving = moving;
        },
        draw(g, t) {
          const sx = worldToScreen(this.x) + this.px;
          const gy = groundAt(this.x + this.px);
          daub(g, sx, gy - 4, 8, 5, 0, P.umber, 0.95);
          g.strokeStyle = P.noir;
          g.globalAlpha = 0.35;
          g.lineWidth = 1;
          g.beginPath();
          g.arc(sx, gy - 3, 6, Math.PI * 1.15, Math.PI * 1.85);
          g.stroke();
          g.globalAlpha = 1;
          const hx = sx + this.dir * (this.moving ? 9.5 : 7.5);
          daub(g, hx, gy - 2.6, 2, 1.6, 0, P.noirWarm, 0.9);
          taper(g, sx - 5, gy - 1, sx - 5, gy + 1, 1.6, P.noirWarm, 0.85, 1);
          taper(g, sx + 5, gy - 1, sx + 5, gy + 1, 1.6, P.noirWarm, 0.85, 1);
        },
      });

      const makeDolphins = (wx, seed) => ({
        kind: 'dolphins',
        x: wx,
        layer: 'water',
        t: hash(seed) * 10,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const off = camX * 0.45 - W * 0.5;
          const seaY = H * 0.655;
          for (let i = 0; i < 2; i++) {
            const ph = (this.t * 0.16 + i * 0.47) % 1;
            if (ph >= 0.4) continue;
            const a = ph / 0.4;
            const ax = this.x - off + i * 150 + a * 110;
            if (ax < -60 || ax > W + 60) continue;
            const ay = seaY - Math.sin(a * Math.PI) * 30;
            const ang = Math.cos(a * Math.PI) * -0.85;
            daub(g, ax, ay, 9, 2.8, ang, P.noirWarm, 0.8);
            taper(g, ax, ay - 2.4, ax - 3, ay - 6.5, 1.6, P.noirWarm, 0.75, 2);
            if (a < 0.16 || a > 0.84) {
              daub(g, ax, seaY + 1, 6, 1.6, 0, P.parchment, 0.7);
              daub(g, ax + 4, seaY - 1, 3, 1, 0.3, P.bone, 0.6);
            }
          }
        },
      });

      const makeRipples = (wx, seed) => ({
        kind: 'ripples',
        x: wx,
        layer: 'water',
        t: hash(seed) * 6,
        onscreen: false,
        update(dt) {
          this.t += dt;
        },
        draw(g, t) {
          const off = camX * 0.45 - W * 0.5;
          const seaY = H * 0.655;
          for (let k = 0; k < 2; k++) {
            const ph = (this.t * 0.22 + k * 0.5) % 1;
            const ax = this.x - off + k * 160;
            if (ax < -40 || ax > W + 40) continue;
            g.strokeStyle = P.bone;
            g.globalAlpha = (1 - ph) * 0.45;
            g.lineWidth = 1.4;
            g.beginPath();
            g.ellipse(ax, seaY + 14, 3 + ph * 20, (3 + ph * 20) * 0.3, 0, 0, TAU);
            g.stroke();
            if (ph < 0.12) daub(g, ax, seaY + 12, 1.6, 1, 0, P.parchment, 0.7);
            g.globalAlpha = 1;
          }
        },
      });

      /* -- build the cast from the areas that exist ------------------- */
      if (AREA.harbor) {
        const hx = AREA.harbor.x;
        AMBIENT.push(makeSailors(hx - 190, 11));
        AMBIENT.push(makeNymph(hx - 305, 12));
        ANIMALS.push(makeDolphins(hx - 210, 13));
        ANIMALS.push(makeRipples(hx - 80, 14));
      }
      if (AREA.grove) {
        const gx = AREA.grove.x;
        AMBIENT.push(makeWorker(gx - 120, 'pick', 'An olive-picker', ['Olives do not hurry. Neither do I.'], 21));
        AMBIENT.push(makeWorker(gx + 160, 'stoop', 'A grove worker', ['Low branches, honest work.'], 22));
        AMBIENT.push(makePair(gx - 240, 'Two friends in conversation', 23));
        ANIMALS.push(makeSheep(gx + 240, 24));
        ANIMALS.push(makeBees(gx - 70, 25));
      }
      if (AREA.amphi) {
        const ax = AREA.amphi.x;
        AMBIENT.push(makeMusician(ax - 100, 31));
        ANIMALS.push(makeGoats(ax + 240, 32));
        ANIMALS.push(makeLizard(ax - 260, 33));
      }
      if (AREA.temple) {
        const tx = AREA.temple.x;
        AMBIENT.push(makePair(tx - 280, 'Two visitors in quiet talk', 41));
        ANIMALS.push(makeDoves(tx - 80, 5, 42));
        ANIMALS.push(makeCat(tx + 100, 43));
      }
      if (AREA.garden) {
        const gx = AREA.garden.x;
        AMBIENT.push(makeArtisan(gx + 220, 51));
        AMBIENT.push(makeStatueAmb(gx + 130, 52));
        ANIMALS.push(makeTortoise(gx - 40, 53));
        ANIMALS.push(makeButterfly(gx - 60, 54));
        ANIMALS.push(makeButterfly(gx + 100, 55));
      }
      if (AREA.sanctuary) {
        const sx2 = AREA.sanctuary.x;
        ANIMALS.push(makeOwl(sx2 - 90, 61));
        ANIMALS.push(makeDoves(sx2 + 180, 4, 62));
      }
      /* travellers walk between areas — pause at each, then move on */
      if (AREA.harbor && AREA.amphi) AMBIENT.push(makeTraveller(AREA.harbor.x + 250, AREA.amphi.x - 100, 62, 0.25, 'A traveller on the road', 71));
      if (AREA.grove && AREA.temple) AMBIENT.push(makeTraveller(AREA.grove.x + 100, AREA.temple.x - 60, 55, 0.6, 'A traveller with a bundle', 72));
      if (AREA.temple && AREA.sanctuary) AMBIENT.push(makeTraveller(AREA.temple.x + 100, AREA.sanctuary.x - 60, 70, 0.1, 'A pilgrim walking east', 73));

      /* seed ambient bark clocks — RARE by design */
      AMBIENT.forEach((am, i) => {
        am.barkArmed = true;
        am.bag = [];
        am.nextBark = performance.now() + 16000 + hash(i * 3.7) * 30000;
      });

      /* ---------- DOM bubbles (barks) */
      const bubbles = []; // {el, npc, until}
      let lastBubbleAt = 0;
      const say = (npc, text, ms) => {
        if (!bubbleHost) return;
        if (bubbles.length >= 2) return;
        const now = performance.now();
        if (now - lastBubbleAt < 2000) return;
        lastBubbleAt = now;
        const el = document.createElement('div');
        el.className = 'realm-bubble';
        el.textContent = text;
        bubbleHost.appendChild(el);
        const entry = { el, npc, until: now + (ms || 4600) };
        bubbles.push(entry);
        A.helpers.announce(npc.name + ': ' + text);
      };

      const positionBubbles = () => {
        let prevRect = null;
        bubbles.forEach((b) => {
          const sx = worldToScreen(b.npc.x);
          const sy = groundAt(b.npc.x) - 78;
          const bw = b.el.offsetWidth;
          let x = clamp(sx - bw / 2, 8, W - bw - 8);
          let y = sy - b.el.offsetHeight;
          if (prevRect && x < prevRect.x + prevRect.w && x + bw > prevRect.x && Math.abs(y - prevRect.y) < prevRect.h + 8) {
            y = prevRect.y - b.el.offsetHeight - 10;
          }
          b.el.style.transform = 'translate3d(' + Math.round(x) + 'px,' + Math.round(y) + 'px,0)';
          prevRect = { x, y, w: bw, h: b.el.offsetHeight };
        });
      };

      /* NPC ambient bark state */
      (DATA.npcs || []).forEach((npc, i) => {
        npc.lines = (npc.lines || []).map((t) => String(t).trim()).filter(Boolean);
        npc.dialogue = (npc.dialogue || []).map((t) => String(t).trim()).filter(Boolean);
        npc.seed = i + 1;
        npc.nextBark = performance.now() + 3000 + hash(i * 7.3) * 9000;
        npc.bag = [];
        npc.armed = true;
      });

      const pullLine = (npc) => {
        if (!npc.lines || !npc.lines.length) return null;
        if (!npc.bag.length) npc.bag = npc.lines.slice().sort(() => Math.random() - 0.5);
        return npc.bag.pop();
      };

      /* ---------- Interaction */
      let nearNpc = null; // proximity/prompt only
      let activeNpc = null; // the captured speaker while the panel is open
      let dialogueOpen = false;
      let dialogueIndex = 0;

      const openDialogue = (npc) => {
        if (!dialoguePanel) return;
        dialogueOpen = true;
        dialogueIndex = 0;
        activeNpc = npc;
        renderDialogue(npc);
        dialoguePanel.hidden = false;
        if (window.AtelierAudio && window.AtelierAudio.event) window.AtelierAudio.event('dialogue');
        const closeBtn = dialoguePanel.querySelector('[data-realm-close]');
        if (closeBtn) closeBtn.focus();
      };

      const renderDialogue = (npc) => {
        const nameEl = dialoguePanel.querySelector('[data-realm-npc-name]');
        const textEl = dialoguePanel.querySelector('[data-realm-npc-text]');
        const nextBtn = dialoguePanel.querySelector('[data-realm-next]');
        const linkEl = dialoguePanel.querySelector('[data-realm-link]');
        const line = (npc.dialogue && npc.dialogue[dialogueIndex]) || '';
        if (nameEl) nameEl.textContent = npc.name;
        if (textEl) textEl.textContent = line;
        const hasMore = npc.dialogue && dialogueIndex < npc.dialogue.length - 1;
        if (nextBtn) nextBtn.hidden = !hasMore;
        if (linkEl) {
          if (!hasMore && npc.linkUrl && npc.linkLabel) {
            linkEl.href = npc.linkUrl;
            linkEl.textContent = npc.linkLabel;
            linkEl.hidden = false;
          } else {
            linkEl.hidden = true;
          }
        }
      };

      const closeDialogue = () => {
        dialogueOpen = false;
        activeNpc = null;
        if (dialoguePanel) dialoguePanel.hidden = true;
        stage.focus();
      };

      if (dialoguePanel) {
        dialoguePanel.addEventListener('click', (e) => {
          if (e.target.closest('[data-realm-close]')) closeDialogue();
          if (e.target.closest('[data-realm-next]') && activeNpc) {
            dialogueIndex += 1;
            renderDialogue(activeNpc);
          }
        });
      }

      if (promptBtn) {
        promptBtn.addEventListener('click', () => {
          if (nearNpc) openDialogue(nearNpc);
        });
      }

      /* ---------- Input (stage-scoped keys — never a global hijack) */
      const keys = { left: false, right: false };
      let walkTarget = null;

      const onKeyDown = (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') keys.left = true;
        else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') keys.right = true;
        else if ((e.key === 'e' || e.key === 'E' || e.key === 'Enter') && nearNpc && !dialogueOpen) {
          if (document.activeElement && document.activeElement.tagName === 'A') return;
          openDialogue(nearNpc);
        } else if (e.key === 'Escape' && dialogueOpen) closeDialogue();
        else return;
        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) e.preventDefault();
        walkTarget = null;
      };
      const onKeyUp = (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') keys.left = false;
        if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') keys.right = false;
      };
      stage.addEventListener('keydown', onKeyDown);
      stage.addEventListener('keyup', onKeyUp);

      const onTap = (e) => {
        if (dialogueOpen) return;
        const rect = canvas.getBoundingClientRect();
        const wx = e.clientX - rect.left - W * 0.5 + camX;
        walkTarget = clamp(wx, 60, WORLD_W - 60);
      };
      canvas.addEventListener('pointerdown', onTap);

      const zones = root.querySelectorAll('[data-realm-zone]');
      zones.forEach((z) => {
        const dir = z.dataset.realmZone === 'left' ? -1 : 1;
        const set = (on) => {
          if (dir < 0) keys.left = on;
          else keys.right = on;
          if (on) walkTarget = null;
        };
        z.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          z.setPointerCapture(e.pointerId);
          set(true);
        });
        z.addEventListener('pointerup', () => set(false));
        z.addEventListener('pointercancel', () => set(false));
      });

      /* ---------- Area label + audio scene */
      const areaLabel = root.querySelector('[data-realm-area]');
      let currentArea = null;

      /* area kind → audio scene key (brief §2a scene names) */
      const AREA_SCENE = {
        harbor: 'harbor',
        gate: 'harbor',
        grove: 'grove',
        amphitheatre: 'amphitheatre',
        temple: 'temple',
        statues: 'garden',
        ruin: 'sanctuary',
      };
      /* area kind → footstep surface */
      const AREA_SURFACE = {
        harbor: 'sand',
        gate: 'sand',
        amphitheatre: 'stone',
        temple: 'stone',
        statues: 'stone',
        ruin: 'stone',
      };
      const surfaceAt = (x) => {
        for (let i = 0; i < (DATA.areas || []).length; i++) {
          const a = DATA.areas[i];
          if (Math.abs(a.x - x) < (a.radius || 420)) return AREA_SURFACE[a.kind] || 'grass';
        }
        return 'grass';
      };

      /* ---------- Frame */
      let elapsed = 0;
      let boilTick = 0;
      let stepAcc = 0;
      let lastFoot = 0;

      const updateEntities = (dt) => {
        const allowsM = A.motion.allows();
        let budget = 12; // cap simultaneously-animated creatures
        ANIMALS.forEach((an) => {
          const sx = an.layer === 'water' ? an.x - (camX * 0.45 - W * 0.5) : worldToScreen(an.x);
          an.onscreen = sx > -170 && sx < W + 170;
          if (!allowsM || !an.onscreen || budget <= 0) return;
          budget -= 1;
          an.update(dt, Math.abs(player.x - an.x));
        });
        AMBIENT.forEach((am) => {
          const sx = worldToScreen(am.x);
          am.onscreen = sx > -150 && sx < W + 150;
          if (!allowsM) return;
          /* travellers keep walking even offscreen so the road stays alive */
          if (am.kind === 'traveller' || am.onscreen) am.update(dt, Math.abs(player.x - am.x));
        });
      };

      const frame = (dt) => {
        elapsed += dt;

        /* movement */
        let move = 0;
        if (keys.left) move -= 1;
        if (keys.right) move += 1;
        if (walkTarget != null) {
          const d = walkTarget - player.x;
          if (Math.abs(d) < 8) walkTarget = null;
          else move = Math.sign(d);
        }
        player.vx = move * SPEED;
        if (move !== 0) {
          player.facing = move;
          player.step += dt * 1.6;
        }
        player.x = clamp(player.x + player.vx * dt, 60, WORLD_W - 60);

        /* footsteps — throttled, surface-aware */
        if (move !== 0) {
          stepAcc += Math.abs(player.vx) * dt;
          if (stepAcc > 46) {
            stepAcc = 0;
            const nowMs = performance.now();
            if (nowMs - lastFoot > 220) {
              lastFoot = nowMs;
              if (window.AtelierAudio && window.AtelierAudio.event) {
                window.AtelierAudio.event('footstep-' + surfaceAt(player.x));
              }
            }
          }
        }

        /* camera */
        const la = player.facing * W * 0.16;
        lookAhead += (la - lookAhead) * (1 - Math.exp(-1.5 * dt));
        const target = player.x + lookAhead;
        if (A.motion.allows()) camX += (target - camX) * (1 - Math.exp(-4 * dt));
        else camX = target;
        camX = clamp(camX, W * 0.5, WORLD_W - W * 0.5);

        /* boil clock ~4Hz on the near plane (full tier only) */
        if (A.motion.allows()) {
          const bt = Math.floor(elapsed * 4) % 2;
          if (bt !== boilTick) boilTick = bt;
        }

        updateEntities(dt);
        render();
        updateWorldDom();
      };

      const drawPlane = (plane, jitter) => {
        const off = camX * plane.factor - W * 0.5;
        const first = Math.floor(off / SEG_W);
        for (let s = first; s * SEG_W < off + W + SEG_W; s++) {
          const c = plane.get(s, jitter);
          ctx.drawImage(c, 0, 0, c.width, c.height, s * SEG_W - off, H - c.height / dpr, SEG_W, c.height / dpr);
        }
      };

      const render = () => {
        /* reduced tier renders a still painting: no sway, no boil */
        const tAnim = A.motion.allows() ? elapsed : 0;
        figRects = [];
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (!skyCanvas) {
          skyCanvas = document.createElement('canvas');
          skyCanvas.width = Math.round(W * dpr);
          skyCanvas.height = Math.round(H * dpr);
          const g = skyCanvas.getContext('2d');
          g.setTransform(dpr, 0, 0, dpr, 0, 0);
          paintSky(g, W, H);
        }
        ctx.drawImage(skyCanvas, 0, 0, skyCanvas.width, skyCanvas.height, 0, 0, W, H);

        drawPlane(farPlane, 0);
        /* haze band between far and mid — cotton morning mist */
        const fog = ctx.createLinearGradient(0, H * 0.5, 0, H * 0.78);
        fog.addColorStop(0, 'rgba(244,242,232,0)');
        fog.addColorStop(0.6, 'rgba(244,242,232,0.2)');
        fog.addColorStop(1, 'rgba(244,242,232,0)');
        ctx.fillStyle = fog;
        ctx.fillRect(0, H * 0.5, W, H * 0.28);

        drawPlane(midPlane, 0);
        /* water life rides the mid parallax (dolphins, fish ripples) */
        ANIMALS.forEach((an) => {
          if (an.layer === 'water' && an.onscreen) an.draw(ctx, tAnim);
        });

        drawPlane(playPlane, A.motion.allows() ? boilTick : 0);

        /* ground creatures on the near plane */
        ANIMALS.forEach((an) => {
          if (an.layer === 'ground' && an.onscreen) an.draw(ctx, tAnim);
        });
        /* the ambient cast */
        AMBIENT.forEach((am) => {
          if (am.onscreen) am.draw(ctx, tAnim);
        });
        /* interactive NPCs + the wanderer read strongest */
        (DATA.npcs || []).forEach((npc) => {
          const sx = worldToScreen(npc.x);
          if (sx < -80 || sx > W + 80) return;
          drawNpc(ctx, npc, sx, groundAt(npc.x), tAnim);
        });
        drawPlayer(ctx, worldToScreen(player.x), groundAt(player.x), tAnim);
        /* airborne creatures over the figures */
        ANIMALS.forEach((an) => {
          if (an.layer === 'air' && an.onscreen) an.draw(ctx, tAnim);
        });

        /* foreground fringe — sparse warm-dark tufts, a light repoussoir */
        const fOff = camX * 1.22 - W * 0.5;
        for (let x = -30; x < W + 30; x += 34) {
          const sd = Math.floor((x + fOff) / 34) * 3.3;
          const s0 = hash(sd);
          if (s0 < 0.3) continue;
          const bx = x + (s0 - 0.5) * 10;
          for (let b = 0; b < 3; b++) {
            const s1 = hash(sd + b * 7.1);
            taper(ctx, bx, H + 4, bx + (s1 - 0.5) * 16, H - 8 - s1 * 15, 2, b === 1 ? P.maroonDark : P.umber, 0.5, 2);
          }
        }

        /* grain + a warm, light-handed vignette */
        if (grainPat) {
          ctx.save();
          ctx.globalAlpha = 0.07;
          ctx.globalCompositeOperation = 'soft-light';
          ctx.fillStyle = grainPat;
          ctx.fillRect(0, 0, W, H);
          ctx.restore();
        }
        const vig = ctx.createRadialGradient(W / 2, H * 0.45, H * 0.35, W / 2, H * 0.45, H * 1.05);
        vig.addColorStop(0, 'rgba(58,36,34,0)');
        vig.addColorStop(1, 'rgba(58,36,34,0.16)');
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, W, H);
      };

      const updateWorldDom = () => {
        const now = performance.now();

        /* expire bubbles */
        for (let i = bubbles.length - 1; i >= 0; i--) {
          if (now > bubbles[i].until || Math.abs(worldToScreen(bubbles[i].npc.x)) > W * 1.4) {
            bubbles[i].el.remove();
            bubbles.splice(i, 1);
          }
        }

        /* NPC barks: proximity hysteresis + cooldowns, full tier only */
        if (A.motion.allows() && !dialogueOpen) {
          (DATA.npcs || []).forEach((npc) => {
            const d = Math.abs(npc.x - player.x);
            if (d > 460) npc.armed = true;
            if (npc.armed && d < 340 && now > npc.nextBark) {
              const line = pullLine(npc);
              if (line) {
                say(npc, line);
                npc.nextBark = now + 8000 + Math.random() * 12000;
                npc.armed = false;
              }
            }
          });
          /* ambient flavor barks — LOW frequency, same bubble discipline */
          AMBIENT.forEach((am) => {
            if (!am.barks || !am.barks.length || !am.onscreen) return;
            const d = Math.abs(am.x - player.x);
            if (d > 320) {
              am.barkArmed = true;
              return;
            }
            if (am.barkArmed && d < 240 && now > am.nextBark) {
              if (!am.bag.length) am.bag = am.barks.slice().sort(() => Math.random() - 0.5);
              say(am, am.bag.pop(), 4200);
              am.nextBark = now + 50000 + Math.random() * 40000;
              am.barkArmed = false;
            }
          });
        }
        positionBubbles();

        /* nearest interactive NPC prompt */
        let nearest = null;
        let nd = 1e9;
        (DATA.npcs || []).forEach((npc) => {
          const d = Math.abs(npc.x - player.x);
          if (d < 130 && d < nd) {
            nd = d;
            nearest = npc;
          }
        });
        if (nearest !== nearNpc) {
          nearNpc = nearest;
          if (promptBtn) {
            if (nearNpc) {
              promptBtn.textContent = (DATA.speakLabel || 'Speak with') + ' ' + nearNpc.name;
              promptBtn.hidden = false;
            } else {
              promptBtn.hidden = true;
              if (dialogueOpen) closeDialogue();
            }
          }
        }
        if (promptBtn && nearNpc && !promptBtn.hidden) {
          const sx = clamp(worldToScreen(nearNpc.x) - promptBtn.offsetWidth / 2, 8, W - promptBtn.offsetWidth - 8);
          promptBtn.style.transform = 'translate3d(' + Math.round(sx) + 'px,' + Math.round(groundAt(nearNpc.x) + 14) + 'px,0)';
        }

        /* area label + ambient audio scene */
        let area = null;
        (DATA.areas || []).forEach((a) => {
          if (Math.abs(a.x - player.x) < (a.radius || 420)) area = a;
        });
        if (area !== currentArea) {
          currentArea = area;
          if (areaLabel) {
            areaLabel.textContent = area ? area.name : DATA.wildsLabel || 'The open country';
            areaLabel.classList.remove('is-fresh');
            void areaLabel.offsetWidth;
            areaLabel.classList.add('is-fresh');
          }
          if (area) A.helpers.announce(area.name);
          if (window.AtelierAudio && window.AtelierAudio.setScene) {
            window.AtelierAudio.setScene(area ? 'realm-' + (AREA_SCENE[area.kind] || 'grove') : 'landscape');
          }
        }
      };

      /* ---------- Debug hooks (QA harness — ?atlDebug=1 only) */
      let debugApi = null;
      if (/[?&]atlDebug=1/.test(window.location.search)) {
        debugApi = {
          teleport(worldX) {
            player.x = clamp(Number(worldX) || 0, 60, WORLD_W - 60);
            walkTarget = null;
            lookAhead = 0;
            camX = clamp(player.x, W * 0.5, Math.max(WORLD_W - W * 0.5, W * 0.5));
            updateEntities(0);
            render();
            updateWorldDom();
          },
          npcRects() {
            /* CSS pixels relative to the stage canvas, latest frame */
            return figRects.map((r) => ({ name: r.name, x: r.x, y: r.y, w: r.w, h: r.h, interactive: r.interactive }));
          },
        };
        window.AtelierRealmDebug = debugApi;
      }

      /* ---------- Lifecycle */
      let stop = null;
      let visible = true;
      const applyMotion = () => {
        /* The realm runs its loop at both tiers (walking must work), but
           reduced tier snaps the camera and disables boil + barks. */
        if (!stop && visible) stop = A.scheduler.add(frame);
      };
      applyMotion();
      const offMotion = A.motion.onChange(applyMotion);

      /* Pause rendering entirely while offscreen. */
      let offView = null;
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            visible = e.isIntersecting;
            if (!visible && stop) {
              stop();
              stop = null;
            } else if (visible && !stop) {
              stop = A.scheduler.add(frame);
            }
          });
        });
        io.observe(stage);
        offView = () => io.disconnect();
      }

      const onResize = A.helpers.onFrame(() => {
        fit();
        render();
      });
      window.addEventListener('resize', onResize, { passive: true });

      updateEntities(0);
      render();
      updateWorldDom();

      return () => {
        if (stop) stop();
        offMotion();
        if (offView) offView();
        window.removeEventListener('resize', onResize);
        stage.removeEventListener('keydown', onKeyDown);
        stage.removeEventListener('keyup', onKeyUp);
        canvas.removeEventListener('pointerdown', onTap);
        bubbles.forEach((b) => b.el.remove());
        bubbles.length = 0;
        if (debugApi && window.AtelierRealmDebug === debugApi) delete window.AtelierRealmDebug;
        if (window.AtelierAudio && window.AtelierAudio.setScene) window.AtelierAudio.setScene('none');
      };
    });
  };

  if (window.Atelier && window.Atelier.__ready) boot(window.Atelier);
  else document.addEventListener('atelier:ready', (e) => boot(e.detail), { once: true });
})();
